import * as d3 from 'd3'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import { endOfWeek, startOfWeek } from 'date-fns'
import { useQuery } from 'react-query'
import {
  ChartData,
  ChartDataExtended,
  ChartDataValue,
  ChartDataValueExtended,
  CPIFrequency,
  CPIIntegrationItem,
  CPILibraryConfigurationIntegration,
  CPIManifest,
  CPISeverity,
  CPITimePeriod,
  CustomTrendsDataPayload,
  D3Position,
  DataPoint,
  DataStatus,
  DateRangeI,
  FrameworkCategoryOption,
  HistoryBenchmarkDataValues,
  HistoryPerformanceDataExtended,
  IntegrationInstance,
  IntegrationStatusMessage,
  Label,
  LabelCount,
  LibraryFilters,
  MetricPointState,
  PerformanceScoreCategoryDetailsDataSources,
  PerformanceScoreCpi,
  PredictionChartData,
  PredictionDataExtended,
  SnowflakeChartData,
  SystemEventName,
  VisibleLabel
} from 'models'
import { Checked } from '../cpi-details/CPIDetails'
import { addUtcDays, getLineCommand, getWeekStartUTC, utcDayDiff } from './chartPointState'
import { dateFormat } from 'lib/utils'
import axios from 'lib/axios'
import { MAX_PINNED_LABELS_COUNT } from '../cpi-details/sections/trends-tab/chart-utils'
import theme from '../../../theme'
import {
  CPIChangeLabelIcon,
  CustomLabelIcon,
  InstanceAddedLabelIcon,
  InstanceRemovedLabelIcon,
  SLAChangeLabelIcon
} from '../../components/svg'

dayjs.extend(weekday)

export const CUSTOM_LABEL_ID = 'io_onyxia_events_cpi_custom'
export const MAX_STRIPES_HISTORY_CHARTS = 5

export function useSearchCpis({
  value,
  framework,
  filters,
  categoryOptions,
  categoryIds,
  reset,
  keepPreviousData,
  enabled,
  staleTime
}: {
  value: string
  framework?: string
  filters: LibraryFilters
  categoryOptions: FrameworkCategoryOption[]
  /* Category ids, if the caller holds them. They replace the title lookup in categoryOptions,
   * and they must stay part of the query key. */
  categoryIds?: string[]
  reset: boolean
  keepPreviousData?: boolean
  enabled?: boolean
  staleTime?: any
}) {
  const sortByFormatted =
    filters.sortBy === 'Newest to Oldest' ? 'desc' : filters.sortBy === 'Oldest to Newest' ? 'asc' : 'favorites'
  let url = `/api/v3/cpis/search?sortBy=${sortByFormatted}`
  if (framework) {
    url += `&framework=${framework}`
  }

  if (!reset) {
    if (value.length) {
      url += `&term=${encodeURIComponent(value)}`
    }
    if (filters.status.length) {
      const statuses = filters.status.join(',')
      url += `&status=${statuses}`
    }
    if (filters.source.length) {
      const sources = filters.source.join(',')
      url += `&ds=${sources}`
    }
    if (filters.frameworkCategory) {
      const { frameworkCategory } = filters
      url += `&category=${frameworkCategory}`
    }
    if (categoryIds?.length) {
      url += `&category=${categoryIds.map((id) => encodeURIComponent(id)).join(',')}`
    } else if (filters.category.length) {
      const filterCategories = filters.category.join(',')
      const selectedCategories = categoryOptions.filter((n) => filterCategories.includes(n.title))
      const selectedIds = selectedCategories.map((n) => n.id)
      url += `&category=${selectedIds}`
    }
  }

  return useQuery(['cpis', { value }, filters, categoryIds ?? []], () => axios.get(url), {
    keepPreviousData,
    /* ONYXIA-3562 */
    // staleTime: 5 * 60 * 1000,
    initialData: undefined,
    // enabled: !keepPreviousData
    enabled,
    staleTime
  })
}

export const monthDiff = (d1: Date, d2: Date) => {
  let months
  months = (d2.getFullYear() - d1.getFullYear()) * 12
  months -= d1.getMonth()
  months += d2.getMonth()

  return months < 0 ? 0 : months + 1
}

export const numDays = (y: number, m: number) => new Date(y, m, 0).getDate()

export const extractMonth = (period: string) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const startMonth = period.slice(0, 3)
  const startYearString = period.slice(9)
  let startYear = Number(`20${startYearString}`)
  const endMonth = period.length > 11 ? period.slice(8, 11) : period.slice(4, 7)
  let endYear = startYear
  const startMonthIndex = months.indexOf(startMonth)
  const endMonthIndex = months.indexOf(endMonth)

  if (period.length > 11) {
    const n = period.slice(5, 7)
    startYear = Number(`20${n}`)
    const c = period.slice(-2)
    endYear = Number(`20${c}`)
  }

  const dateFrom = `${startYear}-${startMonthIndex < 9 ? '0' : ''}${startMonthIndex + 1}-01`
  const endDay = numDays(endYear, endMonthIndex + 1)
  const dateTo = `${endYear}-${endMonthIndex < 9 ? '0' : ''}${endMonthIndex + 1}-${endDay}`

  return {
    dateFrom,
    dateTo
  }
}

export const resolveSectionName = (severity: string, manifestData?: CPIManifest | null): string => {
  const fallback = severity.toLowerCase()
  if (!manifestData) return fallback

  const userInput = manifestData.ui?.trend_chart?.user_input
  if (!userInput) return fallback

  const formulaSections = userInput.formula_params?.[0]?.sections ?? []
  const visualizationSections = userInput.visualization_param?.sections ?? []
  const sections = formulaSections.length > 0 ? formulaSections : visualizationSections

  const byDisplayName = sections.find((s) => s.display_name === severity)
  if (byDisplayName) return byDisplayName.name

  const byName = sections.find((s) => s.name === fallback)
  if (byName) return byName.name

  return fallback
}

export const getPayloadTrendsChart = ({
  severity,
  selectedDataSourceIds,
  selectedTimePeriod,
  selectedFrequency,
  customTimePeriodValue,
  thisType
}: {
  severity: CPISeverity
  selectedDataSourceIds: string[]
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  customTimePeriodValue?: string
  thisType?: string
}) => {
  const timePeriod = getPayloadTimePeriod(selectedTimePeriod)

  const payload: CustomTrendsDataPayload = {
    section: severity,
    integration_ids: selectedDataSourceIds,
    frequency: selectedFrequency
  }
  if (thisType) {
    payload.type = thisType.toLowerCase()
  }

  if (customTimePeriodValue) {
    const { dateFrom, dateTo } = extractMonth(customTimePeriodValue)
    const dateToTemp = new Date(dateTo).getTime()
    const today = new Date().getTime()
    const dateToFormatted = dateToTemp > today ? dayjs().format('YYYY-MM-DD') : dateTo
    payload.dateFrom = dateFrom
    payload.dateTo = dateToFormatted
    payload.stripes = monthDiff(new Date(dateFrom), new Date(dateTo))
  } else {
    payload.timePeriod = timePeriod
  }

  return payload
}

export const getUtcDateAndTime = (date: string, timeZone = 'UTC') => {
  const now = new Date(date)

  const defaultDate = {
    year: 0,
    month: 0,
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
    milliseconds: 0,
    formattedDateTime: date,
    formattedDate: date,
    utcFormattedDate: date,
    isoFormattedDate: date,
    labelFormatDate: date
  }

  if (isNaN(now.getTime())) {
    return defaultDate
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }

  const formattedDateTime = now.toLocaleString('en-US', options)
  const formattedDate = now.toLocaleDateString('en-US')
  const utcFormattedDate = new Date(now).toUTCString()
  const isoFormattedDate = new Date(now).toISOString()
  const labelFormatDate = isoFormattedDate.slice(0, 10)
  const milliseconds = new Date(formattedDate).getTime()

  const [datePart, timePart] = formattedDateTime.split(', ')
  const [monthStr, dayStr, yearStr] = datePart.split('/')
  const [hourStr, minuteStr, secondStr] = timePart.split(':')

  const year = parseInt(yearStr, 10)
  const month = parseInt(monthStr, 10)
  const day = parseInt(dayStr, 10)
  const hour = parseInt(hourStr, 10)
  const minute = parseInt(minuteStr, 10)
  const second = parseInt(secondStr, 10)

  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute) || isNaN(second)) {
    return defaultDate
  }

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    milliseconds,
    formattedDateTime,
    formattedDate,
    utcFormattedDate,
    isoFormattedDate,
    labelFormatDate
  }
}

export const getSystemEventValue = (event: SystemEventName): string => {
  const prunedEvent = event.replaceAll('.', '_')
  switch (prunedEvent) {
    case SystemEventName.CpiActivated:
      return 'CPI Activated'
    case SystemEventName.SlaChangedEvent:
      return 'SLA Changed'
    case SystemEventName.CpiCustom:
      return 'Custom Event'
    case SystemEventName.CpiInstanceAdded:
      return 'Instance Added'
    case SystemEventName.CpiInstanceRemoved:
      return 'Instance Removed'
    default:
      return ''
  }
}

export const getLabelActionProps = (action: SystemEventName) => {
  switch (action) {
    case SystemEventName.CpiInstanceAdded:
      return {
        background: '#402C3F',
        border: `1px solid ${theme.baseColors.primary[60]}`,
        titleColor: theme.colors.labelTitleAdded,
        icon: <InstanceAddedLabelIcon />
      }
    case SystemEventName.CpiInstanceRemoved:
      return {
        background: '#402C3F',
        border: `1px solid ${theme.baseColors.primary[60]}`,
        titleColor: theme.colors.labelTitleRemoved,
        icon: <InstanceRemovedLabelIcon />
      }
    case SystemEventName.SlaChangedEvent:
      return {
        background: '#45312A',
        border: `1px solid ${theme.baseColors.info[40]}`,
        titleColor: theme.colors.labelTitleSla,
        icon: <SLAChangeLabelIcon />
      }
    case SystemEventName.CpiActivated:
      return {
        background: '#393144',
        border: '1px solid #8E9AFF',
        titleColor: '#8E9AFF',
        icon: <CPIChangeLabelIcon />
      }
    case SystemEventName.CpiCustom:
    default:
      return {
        background: '#453837',
        border: `1px solid ${theme.baseColors.info[30]}`,
        titleColor: theme.colors.labelTitleCustom,
        icon: <CustomLabelIcon />
      }
  }
}

export const getPayloadTimePeriod = (period: CPITimePeriod): '1w' | '1m' | '3m' | '6m' | '1y' => {
  if (period === CPITimePeriod.Week) {
    return '1w'
  }
  if (period === CPITimePeriod.Month) {
    return '1m'
  }
  if (period === CPITimePeriod.ThreeMonths) {
    return '3m'
  }
  if (period === CPITimePeriod.SixMonths) {
    return '6m'
  }
  return '1y'
}

export const payloadToEnumTimePeriod = (period: string): CPITimePeriod => {
  if (period === '1w') {
    return CPITimePeriod.Week
  }
  if (period === '1m') {
    return CPITimePeriod.Month
  }
  if (period === '3m') {
    return CPITimePeriod.ThreeMonths
  }
  if (period === '6m') {
    return CPITimePeriod.SixMonths
  }
  return CPITimePeriod.PastYear
}

export const getSlaUnit = (unit?: string) => {
  if (unit) {
    if (unit === 'seconds') {
      return 's'
    }
    if (unit === 'hours') {
      return 'h'
    }
    if (unit === 'percent') {
      return '%'
    }
    if (unit === 'days') {
      return 'd'
    }
  }

  return unit === undefined ? '' : ` ${unit}`
}

export const between = (x: number, min: number, max: number) => {
  return x >= min && x < max
}

export const getSlaFullUnit = (unit?: string) => {
  if (!unit) {
    return ''
  }

  if (unit === 'percent') {
    return '%'
  }

  if (unit === 'out_of_hundred') {
    return '/ 100'
  }

  return unit
}

export const formatPerformanceUnit = (unit?: string, hideTexts = false, capitalize = false) => {
  let suffix = ''

  if (unit) {
    if (hideTexts && ['assets', 'alerts', 'incidents'].includes(unit)) return ''

    switch (unit) {
      case 'percent':
        suffix = '%'
        break
      case 'days':
        suffix = capitalize ? 'D' : 'd'
        break
      case 'seconds':
        suffix = 's'
        break
      case 'hours':
        suffix = 'h'
        break
      case 'out_of_hundred':
        suffix = ' / 100'
        break
      default:
        suffix = ` ${unit}`
        break
    }
  }

  return suffix
}

// The API can send singular spellings and `minutes`.
const COMPACT_UNIT_SYMBOLS: Record<string, string> = {
  hours: 'h',
  hour: 'h',
  minutes: 'm',
  minute: 'm',
  seconds: 's',
  second: 's',
  days: 'd',
  day: 'd',
  percent: '%',
  out_of_hundred: ' / 100'
}

// A symbolic unit measures the value. A display noun names the counted entity.
// Keep the symbol for these units to preserve the meaning of the number.
const SYMBOLIC_UNITS = Object.keys(COMPACT_UNIT_SYMBOLS)

export const formatPerformanceUnitDisplay = (unit?: string, tooltipUnit?: string) => {
  if (!unit || SYMBOLIC_UNITS.includes(unit)) return formatPerformanceUnit(unit)

  return tooltipUnit ? ` ${lowerCaseNoun(tooltipUnit)}` : formatPerformanceUnit(unit)
}

// Acronyms such as 'AI' keep their case; lowercasing them lets `text-transform: capitalize` render 'Ai'.
const lowerCaseNoun = (noun: string) =>
  noun
    .split(' ')
    .map((word) => (/^[A-Z0-9]{2,}$/.test(word) ? word : word.toLowerCase()))
    .join(' ')

// Formats a value for the Calculation Details table. A symbolic unit joins the
// number without a space ('12h'). A word unit uses the display noun and a space.
export const formatCalculationValue = (
  val: number | string | undefined,
  unit: string,
  tooltipUnit?: string
): string => {
  if (val == null) return '-'

  const displayVal = typeof val === 'number' ? Math.round(val) : val
  if (!unit) return `${displayVal}`

  const symbol = COMPACT_UNIT_SYMBOLS[unit.toLowerCase()]
  if (symbol) return `${displayVal}${symbol}`

  return `${displayVal} ${(tooltipUnit || unit).toLowerCase()}`
}

export const urlToCategory = (category: string) => {
  return category.replaceAll('-', ' ').replaceAll('and', '&')
}

export const urlToCategoryCapitalized = (category: string) => {
  const cat = category || ''
  const text = cat.replaceAll('-', ' ').replaceAll('and', '&')
  const split = text.split(' ')

  return split.map((n) => n.charAt(0).toUpperCase() + n.slice(1)).join(' ')
}

export const categoryToUrl = (category: string, isCategory = false) => {
  if (isCategory && category === 'Identity & Access Management') return 'iam'
  const part1 = category.replaceAll(' ', '-')
  const part2 = part1.replaceAll('&', 'and')

  return part2.toLowerCase()
}

// "Threat Detection" (ONYXIA-8279) is shown at the slug `threat-detection` but keeps the
// backend category id `detection-and-response`. Fold the display slug back to the id when
// matching a URL slug against the backend category id.
export const THREAT_DETECTION_SLUG = 'threat-detection'
export const DETECTION_AND_RESPONSE_ID = 'detection-and-response'
export const normalizeCategorySlug = (slug: string) =>
  slug === THREAT_DETECTION_SLUG ? DETECTION_AND_RESPONSE_ID : slug

export const getEnabledInstances = (
  cpiConfigInstances: CPIIntegrationItem[],
  userConfiguredIntegrations: IntegrationInstance[],
  isSnowflake = false
) => {
  const configuredIntegrationsIds = cpiConfigInstances.map((n: CPIIntegrationItem) => n.id)
  const configuredInstancesApplicableToCpi = userConfiguredIntegrations.filter((n) =>
    configuredIntegrationsIds.includes(n.id)
  )
  const configuredIntegrationsNames = isSnowflake
    ? configuredInstancesApplicableToCpi.map((n: CPIIntegrationItem) => n.instance_name || '')
    : configuredInstancesApplicableToCpi.map((n: CPIIntegrationItem) => n.name)
  const newChecked: Checked = {}
  configuredIntegrationsNames.forEach((n) => {
    newChecked[n] = true
  })

  return newChecked
}

export const getConfiguredInstancesApplicableToCpi = (
  configuredIntegrations: CPIIntegrationItem[],
  userConfiguredIntegrations: IntegrationInstance[]
) => {
  const configuredIntegrationsIds = configuredIntegrations.map((n: CPIIntegrationItem) => n.id)

  return userConfiguredIntegrations.filter((n) => configuredIntegrationsIds.includes(n.id))
}

export const getNotConfiguredInstancesApplicableToCpi = (
  manifestData: CPIManifest,
  configuredIntegrations: CPIIntegrationItem[],
  userConfiguredIntegrations: IntegrationInstance[]
) => {
  const configuredIntegrationsIds = configuredIntegrations.map((n: CPIIntegrationItem) => n.id)
  const supportedIntegrationNames = manifestData.supported_integrations.map((n) => n.name)
  const userConfiguredAndCpiSupportedInstances = userConfiguredIntegrations.filter((n) =>
    supportedIntegrationNames.includes(n.integration_name)
  )

  return userConfiguredAndCpiSupportedInstances.filter((n) => !configuredIntegrationsIds.includes(n.id))
}

export const addStartEndWeekly = (data: ChartData): ChartDataExtended => {
  const groupedData: {
    [key: string]: {
      xValue: string
      yValue: number
      start: string
      end: string
      numerator: number
      denominator: number
      pointState?: MetricPointState
    }
  } = {}

  const today = new Date()
  const todayDay = today.getUTCDate()
  const todayMonth = today.getUTCMonth() + 1
  const todayYear = today.getUTCFullYear()

  data.values.forEach((entry) => {
    const date = new Date(entry.xValue)
    const weekStartDay = 1
    const year = parseInt(entry.xValue.slice(0, 4), 10)

    const startOfWeekDate = new Date(date)
    const diffStart = date.getUTCDay() - weekStartDay
    startOfWeekDate.setUTCDate(date.getUTCDate() - diffStart)

    const endOfWeekDate = new Date(startOfWeekDate)
    const diffEnd = 7 - startOfWeekDate.getUTCDay()
    endOfWeekDate.setUTCDate(startOfWeekDate.getUTCDate() + diffEnd)

    let startDay = startOfWeekDate.getUTCDate()
    const startMonth = startOfWeekDate.getUTCMonth() + 1
    if (todayDay < startDay && todayMonth === startMonth && year === todayYear) {
      startDay = todayDay - 6
    }

    const endDay = endOfWeekDate.getUTCDate()
    const endMonth = endOfWeekDate.getUTCMonth() + 1
    const endYear = endOfWeekDate.getUTCFullYear()
    const startYear = startOfWeekDate.getUTCFullYear()

    const groupKey = `${startYear}-W${startMonth < 10 ? '0' : ''}${startMonth}-${startDay < 10 ? '0' : ''}${startDay}`

    if (!groupedData[groupKey]) {
      groupedData[groupKey] = {
        xValue: entry.xValue,
        yValue: entry.yValue,
        numerator: entry.numerator,
        denominator: entry.denominator,
        start: `${startYear}-${startMonth < 10 ? '0' : ''}${startMonth}-${startDay < 10 ? '0' : ''}${startDay}`,
        end: `${endYear}-${endMonth < 10 ? '0' : ''}${endMonth}-${endDay < 10 ? '0' : ''}${endDay}`,
        ...(entry.pointState !== undefined && { pointState: entry.pointState })
      }
    }
  })

  const result: ChartDataValueExtended[] = []
  for (const key in groupedData) {
    result.push({
      xValue: groupedData[key].xValue || '',
      yValue: groupedData[key].yValue,
      start: groupedData[key].start || '',
      end: groupedData[key].end || '',
      numerator: groupedData[key].numerator,
      denominator: groupedData[key].denominator,
      ...(groupedData[key].pointState !== undefined && { pointState: groupedData[key].pointState })
    })
  }

  return { ...data, values: result }
}

export const groupByWeeks = (data: ChartData): ChartDataExtended => {
  const groupedData: {
    [key: string]: {
      xValue: string
      yValue: number
      start: string
      end: string
      numerator: number
      denominator: number
      pointState?: MetricPointState
    }
  } = {}

  const today = new Date()
  const todayDay = today.getUTCDate()
  const todayMonth = today.getUTCMonth() + 1
  const todayYear = today.getUTCFullYear()

  data.values.forEach((entry) => {
    const date = new Date(entry.xValue)
    const year = parseInt(entry.xValue.slice(0, 4), 10)

    const startOfWeekDate = new Date(date)
    /* Monday on or before the point: a Sunday closes its own week rather than opening the next. */
    const diffStart = (date.getUTCDay() + 6) % 7
    startOfWeekDate.setUTCDate(date.getUTCDate() - diffStart)

    const endOfWeekDate = new Date(startOfWeekDate)
    const diffEnd = 7 - startOfWeekDate.getUTCDay()
    endOfWeekDate.setUTCDate(startOfWeekDate.getUTCDate() + diffEnd)

    let startDay = startOfWeekDate.getUTCDate()
    const startMonth = startOfWeekDate.getUTCMonth() + 1
    if (todayDay < startDay && todayMonth === startMonth && year === todayYear) {
      startDay = todayDay - 6
    }

    const endDay = endOfWeekDate.getUTCDate()
    const endMonth = endOfWeekDate.getUTCMonth() + 1
    const endYear = endOfWeekDate.getUTCFullYear()

    const startOfWeekYear = startOfWeekDate.getUTCFullYear()
    const groupKey = `${startOfWeekYear}-W${startMonth < 10 ? '0' : ''}${startMonth}-${
      startDay < 10 ? '0' : ''
    }${startDay}`

    if (groupKey && !groupedData[groupKey]) {
      groupedData[groupKey] = {
        xValue: entry.xValue,
        yValue: entry.yValue,
        numerator: entry.numerator,
        denominator: entry.denominator,
        start: `${startOfWeekYear}-${startMonth < 10 ? '0' : ''}${startMonth}-${startDay < 10 ? '0' : ''}${startDay}`,
        end: `${endYear}-${endMonth < 10 ? '0' : ''}${endMonth}-${endDay < 10 ? '0' : ''}${endDay}`,
        ...(entry.pointState !== undefined && { pointState: entry.pointState })
      }
    }
  })

  const result: ChartDataValueExtended[] = []
  for (const key in groupedData) {
    const prunedXValue = key.replace('W', '')
    const utcValue = getUtcDateAndTime(prunedXValue)

    result.push({
      xValue: utcValue?.isoFormattedDate || '',
      yValue: groupedData[key].yValue,
      start: groupedData[key].start || '',
      end: groupedData[key].end || '',
      numerator: groupedData[key].numerator,
      denominator: groupedData[key].denominator,
      ...(groupedData[key].pointState !== undefined && { pointState: groupedData[key].pointState })
    })
  }

  return { ...data, values: result }
}

export const groupByMonths = (data: ChartData): ChartData => {
  const groupedData: {
    [key: string]: {
      xValue: string
      yValue: number
      denominator: number
      numerator: number
      year: number
      month: number
      week?: number
    }
  } = {}

  data.values.forEach((entry) => {
    const { month, year } = getUtcDateAndTime(entry.xValue)
    const key = `${year}-${month < 10 ? '0' : ''}${month}`

    if (!groupedData[key]) {
      groupedData[key] = {
        year,
        month,
        xValue: entry.xValue || '',
        yValue: entry.yValue || 0,
        denominator: entry.denominator || 0,
        numerator: entry.numerator || 0
      }
    }
  })

  const result: ChartDataValue[] = []
  for (const key in groupedData) {
    const groupEntries = data.values.filter((entry) => entry.xValue.startsWith(key))

    result.push({ ...groupEntries[0] })
  }

  return { ...data, values: result }
}

export function convertDateToUTC(date: Date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  )
}
export function convertDateToUTCString(date: Date) {
  return convertDateToUTC(date).toUTCString()
}

export const filterDateByMonth = (
  data: ChartDataValue[],
  month: string
): ChartDataValue[] | ChartDataValueExtended[] => {
  const curDat = data.filter((n) => n.xValue.includes(month))
  if (!curDat.length) return []
  const currentDate = getUtcDateAndTime(curDat[0].xValue).utcFormattedDate
  const { month: currentMonth, year: currentYear } = getUtcDateAndTime(currentDate)

  return data.filter((e) => {
    const thisMonth = getUtcDateAndTime(e.xValue).month
    const thisYear = getUtcDateAndTime(e.xValue).year

    return thisMonth === currentMonth && thisYear === currentYear
  })
}

export const filterDateByMonthPredictions = (
  data: PredictionDataExtended[],
  month: string
): PredictionDataExtended[] => {
  const curDat = data.filter((n) => n.xValue.includes(month))
  if (!curDat.length) return []
  const currentDate = getUtcDateAndTime(curDat[0].xValue).utcFormattedDate
  const { month: currentMonth, year: currentYear } = getUtcDateAndTime(currentDate)

  return data.filter((e) => {
    const thisMonth = getUtcDateAndTime(e.xValue).month
    const thisYear = getUtcDateAndTime(e.xValue).year

    return thisMonth === currentMonth && thisYear === currentYear
  })
}

/*
 * add start and end to each date in data set.
 * return original dataset with start and end prop to each item in dataset
 * */
export const addWeekBreakpoints = (data: ChartDataValue[]): ChartDataValueExtended[] => {
  return data.map((entry) => {
    const { year } = getUtcDateAndTime(entry.xValue)
    const date = new Date(entry.xValue)
    const weekStartDay = 1

    const startOfWeekDate = new Date(date)
    const diffStart = date.getUTCDay() - weekStartDay
    startOfWeekDate.setUTCDate(date.getUTCDate() - (diffStart < 0 ? 6 : diffStart))

    const endOfWeekDate = new Date(startOfWeekDate)
    const diffEnd = 7 - startOfWeekDate.getUTCDay()
    endOfWeekDate.setUTCDate(startOfWeekDate.getUTCDate() + diffEnd)
    const startDay = startOfWeekDate.getUTCDate()
    const startMonth = startOfWeekDate.getUTCMonth() + 1

    const endDay = endOfWeekDate.getUTCDate()
    const endMonth = endOfWeekDate.getUTCMonth() + 1
    const endYear = endOfWeekDate.getUTCFullYear()

    return {
      xValue: entry.xValue,
      yValue: entry.yValue,
      numerator: entry.numerator,
      denominator: entry.denominator,
      start: `${year}-${startMonth < 10 ? '0' : ''}${startMonth}-${startDay < 10 ? '0' : ''}${startDay}`,
      end: `${endYear}-${endMonth < 10 ? '0' : ''}${endMonth}-${endDay < 10 ? '0' : ''}${endDay}`
    }
  })
}

export const filterDateByWeekWithStartEnd = (data: ChartDataValue[], start: string, end: string): ChartDataValue[] => {
  const startTime = new Date(start).getTime()
  const endTime = new Date(end).getTime()

  return data.filter((e) => {
    const currentDate = getUtcDateAndTime(e.xValue).utcFormattedDate
    const currentTime = new Date(currentDate).getTime()
    const currentDayTime = new Date(currentTime).getTime()

    return currentDayTime >= startTime && currentDayTime <= endTime
  })
}

export const filterDateByWeek = (data: ChartDataValue[], day: string): ChartDataValue[] => {
  const date = new Date(day)
  const weekStartDay = 1
  const dayOfWeek = dayjs(date).weekday()

  const startOfWeekDate = new Date(date)
  const diffStart = date.getUTCDay() - weekStartDay
  if (dayOfWeek !== 1) {
    startOfWeekDate.setUTCDate(date.getUTCDate() - diffStart)
  }

  const endOfWeekDate = new Date(startOfWeekDate)
  const diffEnd = 7 - startOfWeekDate.getUTCDay()
  endOfWeekDate.setUTCDate(startOfWeekDate.getUTCDate() + diffEnd)

  return data.filter((n) => {
    const current = new Date(n.xValue)

    return current >= startOfWeekDate && current <= endOfWeekDate
  })
}

export const filterDateByDay = (data: ChartDataValue[], day: string): ChartDataValue[] => {
  const currentDate = new Date(day).getUTCDate()

  return data.filter((e: any) => {
    const thisDate = new Date(e.xValue).getUTCDate()

    return thisDate === currentDate
  })
}

/*
 * Typical horizontal distance between neighbouring points. Used as the proximity bound below: the
 * median keeps one irregular stripe boundary from widening it for the whole chart.
 * */
export const getPointProximityLimit = (
  positions: D3Position[],
  stripeWidth: number,
  windowStripeWidth = stripeWidth
): number => {
  const gaps: number[] = []
  for (let i = 1; i < positions.length; i++) {
    const gap = positions[i].x - positions[i - 1].x
    if (gap > 0) gaps.push(gap)
  }
  /*
   * A lone point has no gap to measure. One stripe is the bound, but taken from the window rather
   * than the drawn domain: a domain the response collapsed to a single stripe would otherwise put
   * the bound across the whole chart, which is the unbounded reach ONYXIA-8706 removed.
   * */
  if (!gaps.length) return Math.min(stripeWidth, windowStripeWidth)

  gaps.sort((a, b) => a - b)

  return gaps[Math.floor(gaps.length / 2)]
}

/*
 * maxDistance bounds how far the cursor may sit from a point and still resolve to it. Left unbounded
 * the nearest point wins at any distance, so hovering a stretch the source never reported reads back
 * the first point that does exist, from a different period entirely (ONYXIA-8706).
 * */
export const findNearestXIndex = (
  array: D3Position[],
  targetX: number,
  healthUnit?: string,
  maxDistance = Infinity
): DataPoint | null => {
  let minDiff = Infinity
  let nearestObject = null

  for (let i = 0; i < array.length; i++) {
    const diff = Math.abs(array[i].x - targetX)

    if (diff < minDiff) {
      minDiff = diff
      nearestObject = {
        x: array[i].x,
        y: array[i].y,
        value: healthUnit && healthUnit === 'percent' ? Math.round(array[i].value as number) : array[i].value,
        index: i,
        monthIndex: array[i].monthIndex
      }
    }
  }

  if (minDiff > maxDistance) return null

  return nearestObject
}

export const findUniqueMonths = ({
  filteredData,
  groupedByWeeks,
  selectedTimePeriod
}: {
  filteredData: ChartDataValue[]
  groupedByWeeks: ChartDataExtended | null
  selectedTimePeriod: CPITimePeriod
}): string[] => {
  const uniqueMonths: string[] = []

  if (selectedTimePeriod === CPITimePeriod.Week) {
    filteredData.forEach((item) => {
      const { month, year, day } = getUtcDateAndTime(item.xValue)
      const monthKey = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

      if (!uniqueMonths.includes(monthKey)) {
        uniqueMonths.push(monthKey)
      }
    })
  } else if (selectedTimePeriod === CPITimePeriod.Month && groupedByWeeks) {
    return groupedByWeeks.values.map((n) => n.xValue.slice(0, 10))
  } else {
    filteredData.forEach((item) => {
      const { month, year } = getUtcDateAndTime(item.xValue)
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`

      if (!uniqueMonths.includes(monthKey)) {
        uniqueMonths.push(monthKey)
      }
    })
  }

  return uniqueMonths
}

export const findUniqueMonthsPredictions = ({ filteredData }: { filteredData: PredictionDataExtended[] }): string[] => {
  const uniqueMonths: string[] = []

  filteredData.forEach((item) => {
    const { month, year } = getUtcDateAndTime(item.xValue)
    const monthKey = `${year}-${month.toString().padStart(2, '0')}`

    if (!uniqueMonths.includes(monthKey)) {
      uniqueMonths.push(monthKey)
    }
  })

  return uniqueMonths
}

export const findUniqueMonthsInitializing = (
  filteredData: ChartDataValue[],
  timePeriod: CPITimePeriod,
  data?: ChartData
): string[] => {
  const uniqueMonths: string[] = []

  if (timePeriod === CPITimePeriod.Week) {
    filteredData.forEach((item: any) => {
      const date = new Date(item.xValue)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      const monthKey = `${year}-${month.toString().padStart(2, '0')}-${day}`

      if (!uniqueMonths.includes(monthKey)) {
        uniqueMonths.push(monthKey)
      }
    })
  } else if (timePeriod === CPITimePeriod.Month && data) {
    return filteredData
      .map((n) => dayjs(n.xValue).format('YYYY-MM-DD'))
      .sort((a, b) => (new Date(b).getTime() > new Date(a).getTime() ? -1 : 1))
  } else {
    filteredData.forEach((item: any) => {
      const date = new Date(item.xValue)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`

      if (!uniqueMonths.includes(monthKey)) {
        uniqueMonths.push(monthKey)
      }
    })
  }

  return uniqueMonths.sort((a, b) => (new Date(b).getTime() > new Date(a).getTime() ? -1 : 1))
}

const WINDOW_MONTHS_BY_TIME_PERIOD: Partial<Record<CPITimePeriod, number>> = {
  [CPITimePeriod.ThreeMonths]: 3,
  [CPITimePeriod.SixMonths]: 6,
  [CPITimePeriod.PastYear]: 12
}

const pad2 = (value: number): string => value.toString().padStart(2, '0')

const toMonthKey = (dateISO: string): string => dateISO.slice(0, 7)

const shiftMonthKey = (monthKey: string, months: number): string => {
  const [year, month] = monthKey.split('-').map(Number)
  const total = year * 12 + (month - 1) + months

  return `${Math.floor(total / 12)}-${pad2((total % 12) + 1)}`
}

/* First calendar day the selected period covers: whole months back from today, plus the running month. */
const windowStartISO = (selectedTimePeriod: CPITimePeriod, todayISO: string): string => {
  const months = selectedTimePeriod === CPITimePeriod.Month ? 1 : WINDOW_MONTHS_BY_TIME_PERIOD[selectedTimePeriod] ?? 0

  return months ? `${shiftMonthKey(toMonthKey(todayISO), -months)}-01` : ''
}

const buildDayKeys = (fromISO: string, toISO: string): string[] => {
  const keys: string[] = []
  for (let cursor = fromISO; cursor <= toISO; cursor = addUtcDays(cursor, 1)) {
    keys.push(cursor)
  }

  return keys
}

const buildWeekKeys = (fromISO: string, toISO: string): string[] => {
  const keys: string[] = []
  for (let cursor = getWeekStartUTC(fromISO); cursor <= toISO; cursor = addUtcDays(cursor, 7)) {
    keys.push(cursor)
  }

  return keys
}

const buildMonthKeys = (fromKey: string, toKey: string): string[] => {
  const keys: string[] = []
  for (let cursor = fromKey; cursor <= toKey; cursor = shiftMonthKey(cursor, 1)) {
    keys.push(cursor)
  }

  return keys
}

/*
 * Stripe keys spanning the whole selected period; a sparse response cannot widen the axis on its own.
 * Periods reach back whole calendar months, the same window filterData uses: 3M in mid-August starts
 * at May, not 90 days back, and 1M's week grid opens on the 1st of the previous month.
 */
export const findWindowStripes = ({
  selectedTimePeriod,
  selectedFrequency,
  customTimePeriodValue,
  lastDataDate,
  today = new Date()
}: {
  selectedTimePeriod: CPITimePeriod
  selectedFrequency?: CPIFrequency
  customTimePeriodValue?: string | null
  lastDataDate?: string
  today?: Date
}): string[] => {
  const todayISO = today.toISOString().slice(0, 10)
  /* Trends run through yesterday, but a Ready response may already carry today. */
  const yesterdayISO = addUtcDays(todayISO, -1)
  const lastISO = lastDataDate ? lastDataDate.slice(0, 10) : yesterdayISO
  const windowEnd = lastISO > yesterdayISO ? lastISO : yesterdayISO

  if (selectedTimePeriod === CPITimePeriod.Week) {
    return buildDayKeys(addUtcDays(windowEnd, -6), windowEnd)
  }

  if (selectedTimePeriod === CPITimePeriod.Month) {
    /* A weekly axis carries the running week, so it reaches today even before today's point lands. */
    const weekWindowEnd = selectedFrequency === CPIFrequency.Weekly && todayISO > windowEnd ? todayISO : windowEnd

    return buildWeekKeys(windowStartISO(selectedTimePeriod, todayISO), weekWindowEnd)
  }

  if (selectedTimePeriod === CPITimePeriod.Custom) {
    if (!customTimePeriodValue) return []
    const { dateFrom, dateTo } = extractMonth(customTimePeriodValue)

    return buildMonthKeys(toMonthKey(dateFrom), toMonthKey(dateTo))
  }

  const windowStart = windowStartISO(selectedTimePeriod, todayISO)
  if (!windowStart) return []

  return buildMonthKeys(toMonthKey(windowStart), toMonthKey(windowEnd))
}

/* Widen a data-derived stripe domain to the window without ever dropping a stripe that holds data. */
export const padStripesToWindow = (dataStripes: string[], windowStripes: string[]): string[] =>
  Array.from(new Set([...dataStripes, ...windowStripes])).sort()

/* First calendar day a stripe covers, as YYYY-MM-DD. Day and week stripes are already keyed by it. */
export const getStripeStartDate = (stripeKey: string, selectedTimePeriod: CPITimePeriod): string =>
  selectedTimePeriod === CPITimePeriod.Week || selectedTimePeriod === CPITimePeriod.Month
    ? stripeKey.slice(0, 10)
    : `${stripeKey}-01`

/*
 * Function to filter data for the given number of months
 * */
export const filterData = (months: number, data: any) => {
  const today = new Date()
  const utcStartDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - months, 1))

  return data.values.filter((item: any) => {
    const itemDate = new Date(item.xValue)
    const utcItemDate = new Date(Date.UTC(itemDate.getUTCFullYear(), itemDate.getUTCMonth(), itemDate.getUTCDate()))

    return utcItemDate >= utcStartDate
  })
}

export const filterPredictionData = (months: number, data: PredictionChartData): PredictionDataExtended[] => {
  const today = new Date(data.values[data.values.length - 1].xValue)
  const utcStartDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - months, 1))

  return data.values.filter((item) => {
    const itemDate = new Date(item.xValue)
    const utcItemDate = new Date(Date.UTC(itemDate.getUTCFullYear(), itemDate.getUTCMonth(), itemDate.getUTCDate()))

    return utcItemDate >= utcStartDate
  })
}

export const getChartWidth = (
  isSmall: boolean,
  isLaptop: boolean,
  isLargeScreen: boolean,
  isExtraLargeScreen: boolean
) => {
  return isSmall ? 700 : isLaptop ? 800 : isLargeScreen ? 920 : isExtraLargeScreen ? 1100 : 1638
}

export const getChartHeight = (isSmallHeight: boolean, isMediumHeight: boolean, isBigHeight: boolean) => {
  return isSmallHeight ? 499 : isMediumHeight ? 500 : isBigHeight ? 567 : 567
}

export const dateStringToUTC = (date: string) => {
  const d = new Date(date)
  const day = d.getUTCDate()
  const month = d.getUTCMonth() + 1
  const year = d.getUTCFullYear()

  return `${year}-${month}-${day}`
}

export const formatTooltipDate = (
  start: string,
  end: string,
  frequency: CPIFrequency,
  timePeriod: CPITimePeriod,
  dataStartsAt: string
) => {
  if (frequency === CPIFrequency.Daily) {
    const res = dateStringToUTC(start)
    return dayjs(res).format('MMMM DD, YYYY')
  }

  if (frequency === CPIFrequency.Weekly) {
    const { rangeFromFormatted, rangeToFormatted } = getRangeFromToFormatted(frequency, dataStartsAt, start, end)

    return !rangeToFormatted ? rangeFromFormatted : `${rangeFromFormatted}-${rangeToFormatted}`
  }

  if (frequency === CPIFrequency.Monthly) {
    return dayjs(start).format('MMMM YYYY')
  }

  return ''
}

export const getRangeFromToFormattedInsights = (start: string, end: string) => {
  const utcDateStart = getUtcDateAndTime(start)
  const utcDateEnd = getUtcDateAndTime(end)
  const rangeFrom = utcDateStart.labelFormatDate
  const rangeFromMonth = utcDateStart.month
  const rangeFromYear = utcDateStart.year
  const rangeToMonth = utcDateEnd.month
  const rangeToYear = utcDateEnd.year
  const isSameYear = rangeFromYear === rangeToYear
  const isSameMonth = rangeFromMonth === rangeToMonth
  const rangeFromFormatted = dayjs(rangeFrom).format(`MMM D${!isSameYear ? ', YYYY' : ''}`)
  let rangeToFormatted = dayjs(end).format(`${!isSameMonth ? 'MMM ' : ''}D${isSameYear ? ', YYYY' : ''}`)

  if (rangeFromFormatted.slice(-2) === rangeToFormatted) {
    rangeToFormatted = ''
  }

  return {
    rangeFromFormatted,
    rangeToFormatted
  }
}

export const formatTooltipDateInsights = (start: string, end: string) => {
  const { rangeFromFormatted, rangeToFormatted } = getRangeFromToFormattedInsights(start, end)

  return !rangeToFormatted ? rangeFromFormatted : `${rangeFromFormatted}-${rangeToFormatted}`
}

/*
 *  The final stripe is normally partial, so laying its points across the whole stripe would space
 *  them further apart than in the full stripes before it. Returns the fraction of the stripe the
 *  points should occupy so the interval between consecutive points stays constant across the chart.
 * */
export const getLastStripeFraction = ({
  lastValue,
  pointsInLastStripe,
  selectedTimePeriod,
  selectedFrequency
}: {
  lastValue: string | number
  pointsInLastStripe: number
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
}): number => {
  const getPointsInFullStripe = () => {
    // 1W draws a stripe per day and monthly data a point per stripe, so neither can be partial
    if (selectedTimePeriod === CPITimePeriod.Week || selectedFrequency === CPIFrequency.Monthly) return 1
    // 1M draws a stripe per week, every other time period draws a stripe per month
    const daysInStripe = selectedTimePeriod === CPITimePeriod.Month ? 7 : dayjs(lastValue).daysInMonth()

    return selectedFrequency === CPIFrequency.Daily ? daysInStripe : daysInStripe / 7
  }

  const pointsInFullStripe = getPointsInFullStripe()

  return pointsInFullStripe > 0 ? Math.min(pointsInLastStripe / pointsInFullStripe, 1) : 1
}

export const getPathAndPositionsOC = ({
  uniqueMonths,
  stripeWidth,
  height,
  filteredData,
  groupedByWeeks,
  selectedTimePeriod,
  selectedFrequency,
  yAxisDomain,
  isReady,
  chartHeightStart
}: {
  uniqueMonths: string[]
  stripeWidth: number
  height: number
  filteredData: ChartDataValue[]
  groupedByWeeks: ChartDataExtended | null
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  yAxisDomain: [number, number]
  isReady: boolean
  chartHeightStart?: number
}): [string, Array<D3Position>, D3Position, Array<D3Position>] => {
  let pathStr = ``
  const positions: D3Position[] = []
  /* Same as getPathAndPositions: a moveto no lineto extends paints nothing. */
  const isolatedPositions: D3Position[] = []
  let openSubpathStart: D3Position | null = null
  let hasDrawnPoint = false
  let hasDummyData = false
  let pathStartPoint = { x: 0, y: 0 }
  let weeklyGroupedValues: ChartDataValueExtended[] = []
  if (groupedByWeeks) {
    weeklyGroupedValues = groupedByWeeks.values
  }
  let filteredDataStringified = [...filteredData]
  if (typeof filteredDataStringified[0].xValue !== 'string') {
    filteredDataStringified = filteredDataStringified.map((n) => {
      return {
        ...n,
        xValue: dayjs(n.xValue).format(dateFormat.customLabel)
      }
    })
  }

  const lastValue = filteredDataStringified[filteredDataStringified.length - 1].xValue

  for (let i = 0; i < uniqueMonths.length; i++) {
    const stripeWidthStart = stripeWidth * i

    let groupedMonths: ChartDataValue[] = []
    if (selectedTimePeriod === CPITimePeriod.Week) {
      const currentDate = getUtcDateAndTime(uniqueMonths[i])
      groupedMonths = filterDateByDay(filteredDataStringified, currentDate.utcFormattedDate)
    } else if (selectedTimePeriod === CPITimePeriod.Month && groupedByWeeks) {
      if (selectedFrequency === CPIFrequency.Daily) {
        let preparedData = addWeekBreakpoints(filteredDataStringified)
        if (typeof preparedData[0].xValue !== 'string') {
          preparedData = preparedData.map((n) => {
            return {
              ...n,
              xValue: dayjs(n.xValue).format(dateFormat.customLabel)
            }
          })
        }

        const d = preparedData.find((n) => n.start === dayjs(uniqueMonths[i]).format(dateFormat.customLabel))

        if (d) {
          groupedMonths = filterDateByWeekWithStartEnd(filteredDataStringified, (d as any).start, (d as any).end)
        } else {
          groupedMonths = filterDateByWeek(filteredDataStringified, uniqueMonths[i])
        }
      } else {
        const preparedData = addWeekBreakpoints(weeklyGroupedValues)
        const d = preparedData.find((n) => n.start === dayjs(uniqueMonths[i]).format(dateFormat.customLabel))
        if (d) {
          groupedMonths = filterDateByWeekWithStartEnd(weeklyGroupedValues, (d as any).start, (d as any).end)
        } else {
          groupedMonths = filterDateByWeek(weeklyGroupedValues, uniqueMonths[i])
        }
      }
    } else {
      const month = dayjs(uniqueMonths[i]).format('YYYY-MM')
      groupedMonths = filterDateByMonth(
        selectedFrequency === CPIFrequency.Weekly ? weeklyGroupedValues : filteredDataStringified,
        month
      )
    }

    /*
     * A placeholder position here would desync the tooltip's positions-to-data index mapping, and
     * its moveto to the left edge would draw the line in from outside the data.
     * */
    if (!groupedMonths.length) continue

    const stripeFraction =
      isReady && i === uniqueMonths.length - 1
        ? getLastStripeFraction({
            lastValue,
            pointsInLastStripe: groupedMonths.length,
            selectedTimePeriod,
            selectedFrequency
          })
        : 1

    const xScaleEach = d3
      .scaleBand()
      .domain(groupedMonths.map((d: ChartDataValue) => d.xValue))
      .range([stripeWidthStart, stripeWidthStart + stripeWidth * stripeFraction])
      .padding(0)
    const stripeMiddle = !isReady && i === 0 ? 0 : xScaleEach.bandwidth() / 2
    const yScaleEach = d3
      .scaleLinear()
      .domain(yAxisDomain)
      .nice()
      .range([height, chartHeightStart ?? 0])

    groupedMonths.forEach((each: ChartDataValue) => {
      /* Whichever point comes first opens the path: the window can start on stripes with no data. */
      let startString = hasDrawnPoint ? 'L' : 'M'
      const add = isReady ? stripeMiddle : stripeMiddle * 2
      const isoString = new Date(each.xValue).toISOString()
      const d = isoString.slice(0, 10) || dayjs(each.xValue).format('YYYY-MM-DD')
      const position = {
        x: (xScaleEach(each.xValue) || 0) + add,
        y: yScaleEach(each.yValue),
        value: each.yValue,
        // date: each.xValue,
        date: dayjs(d).format('YYYY-MM-DD'),
        monthIndex: i,
        hasDummyData
      }
      if (!hasDrawnPoint) {
        pathStartPoint = position
      }
      if (each.dummy) {
        hasDummyData = true
      } else {
        if (hasDummyData) {
          pathStartPoint = position
          startString = 'M'
          hasDummyData = false
        }
        pathStr += `${startString}${position.x} ${position.y}`
      }

      if (startString === 'M') {
        if (openSubpathStart) isolatedPositions.push(openSubpathStart)
        openSubpathStart = position
      } else {
        openSubpathStart = null
      }

      positions.push(position)
      pathStr += `${startString}${position.x} ${position.y}`
      hasDrawnPoint = true
    })
  }

  if (openSubpathStart) isolatedPositions.push(openSubpathStart)

  return [pathStr, positions, pathStartPoint, isolatedPositions]
}

/*
 *  Populate each stripe with data points for that month
 * */
/* Days one stripe spans: 1W draws a stripe per day, 1M one per week, every other period one per month. */
const getStripeSpanDays = (stripeKey: string, selectedTimePeriod: CPITimePeriod): number => {
  if (selectedTimePeriod === CPITimePeriod.Week) return 1
  if (selectedTimePeriod === CPITimePeriod.Month) return 7

  return dayjs(getStripeStartDate(stripeKey, selectedTimePeriod)).daysInMonth()
}

const getPointSpanDays = (spanDays: number, selectedFrequency: CPIFrequency): number => {
  if (selectedFrequency === CPIFrequency.Monthly) return spanDays

  /* A point never spans more than its own stripe, and 1W draws a stripe per day. */
  return Math.min(selectedFrequency === CPIFrequency.Weekly ? 7 : 1, spanDays)
}

/*
 * Place each point by its date rather than by its index among the points present in the stripe. The
 * response carries only the days a source has, so an index-based scale slides the first present
 * point onto the stripe's first slot. A dense stripe is laid out identically either way.
 */
const buildDateAnchoredX = ({
  stripeKey,
  stripeWidthStart,
  stripeWidth,
  selectedTimePeriod,
  selectedFrequency,
  weeklyBuckets = []
}: {
  stripeKey: string
  stripeWidthStart: number
  stripeWidth: number
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  weeklyBuckets?: ChartDataValue[]
}): ((each: ChartDataValue) => number) => {
  const spanDays = getStripeSpanDays(stripeKey, selectedTimePeriod)
  const pointSpanDays = getPointSpanDays(spanDays, selectedFrequency)
  const stripeStart = getStripeStartDate(stripeKey, selectedTimePeriod)
  /* A point covering its whole stripe has nowhere to sit but the middle of it. */
  const fillsStripe = pointSpanDays >= spanDays

  /*
   * One slot per week the stripe's label owns — the boundary week included, or the month's first
   * bucket lands on its neighbour's slot and hides it from the hover. Every owned week counts, so a
   * month missing its early weeks leaves those slots empty.
   */
  if (selectedFrequency === CPIFrequency.Weekly && !fillsStripe) {
    const [gridStart, gridEnd] = monthWeekSpan(stripeKey, weeklyBuckets)
    const firstSlot = gridStart
    const slotCount = (utcDayDiff(gridStart, gridEnd) + 1) / 7

    return (each: ChartDataValue): number => {
      /* A bucket's first present day slides with the gaps; its week start does not. */
      const weekStart = getWeekStartUTC(((each as ChartDataValueExtended).start || each.xValue).slice(0, 10))
      const rawSlot = Math.round(utcDayDiff(firstSlot, weekStart) / 7)
      const slot = Math.min(Math.max(rawSlot, 0), slotCount - 1)

      return stripeWidthStart + (stripeWidth * (slot + 0.5)) / slotCount
    }
  }

  const centerFraction = selectedFrequency === CPIFrequency.Daily && selectedTimePeriod !== CPITimePeriod.Week ? 0 : 0.5

  return (each: ChartDataValue): number => {
    const pointDate = ((each as ChartDataValueExtended).start || each.xValue).slice(0, 10)
    const offsetDays = fillsStripe ? 0 : Math.max(utcDayDiff(stripeStart, pointDate), 0)

    return stripeWidthStart + (stripeWidth * (offsetDays + centerFraction * pointSpanDays)) / spanDays
  }
}

/* Initializing and Recalculating render a synthetic monthly-spaced series; keep its slot placement. */
const buildStubBandX = ({
  groupedMonths,
  stripeWidthStart,
  stripeWidth,
  selectedTimePeriod,
  selectedFrequency,
  isFirstStripe
}: {
  groupedMonths: ChartDataValue[]
  stripeWidthStart: number
  stripeWidth: number
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  isFirstStripe: boolean
}): ((each: ChartDataValue) => number) => {
  const xScaleEach = d3
    .scaleBand()
    .domain(groupedMonths.map((d: ChartDataValue) => d.xValue))
    .range([stripeWidthStart, stripeWidthStart + stripeWidth])
    .padding(0)
  const stripeMiddle = isFirstStripe ? 0 : xScaleEach.bandwidth() / 2
  const add =
    selectedFrequency === CPIFrequency.Daily && selectedTimePeriod !== CPITimePeriod.Week ? 0 : stripeMiddle * 2

  return (each: ChartDataValue): number => (xScaleEach(each.xValue) || 0) + add
}

export const getPathAndPositions = ({
  uniqueMonths,
  stripeWidth,
  height,
  filteredData,
  groupedByWeeks,
  selectedTimePeriod,
  selectedFrequency,
  yAxisDomain,
  dataStatus,
  chartHeightStart
}: {
  uniqueMonths: string[]
  stripeWidth: number
  height: number
  filteredData: ChartDataValue[]
  groupedByWeeks: ChartDataExtended | null
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  yAxisDomain: [number, number]
  chartHeightStart?: number
  dataStatus: DataStatus
}): [string, Array<D3Position>, Array<D3Position>] => {
  let pathStr = ``
  const positions: D3Position[] = []
  /* A moveto no lineto ever extends paints nothing, so the point it sits on needs a marker of its own. */
  const isolatedPositions: D3Position[] = []
  let openSubpathStart: D3Position | null = null
  let weeklyGroupedValues: ChartDataValueExtended[] = []
  if (groupedByWeeks) {
    weeklyGroupedValues = groupedByWeeks.values
  }

  let prevPointDate: string | null = null

  for (let i = 0; i < uniqueMonths.length; i++) {
    const stripeWidthStart = stripeWidth * i

    let groupedMonths: ChartDataValue[] = []
    if (dataStatus === DataStatus.Initializing) {
      groupedMonths = filteredData
    } else if (selectedTimePeriod === CPITimePeriod.Week) {
      const currentDate = getUtcDateAndTime(uniqueMonths[i])
      groupedMonths = filterDateByDay(filteredData, currentDate.utcFormattedDate)
    } else if (selectedTimePeriod === CPITimePeriod.Month && groupedByWeeks) {
      if (selectedFrequency === CPIFrequency.Daily) {
        const preparedData = addWeekBreakpoints(filteredData)
        const d = preparedData.find((n) => n.start === dayjs(uniqueMonths[i]).format(dateFormat.customLabel))

        if (d) {
          groupedMonths = filterDateByWeekWithStartEnd(filteredData, (d as any).start, (d as any).end)
        } else {
          groupedMonths = filterDateByWeek(filteredData, uniqueMonths[i])
        }
      } else {
        const preparedData = addWeekBreakpoints(weeklyGroupedValues)
        const d = preparedData.find((n) => n.start === dayjs(uniqueMonths[i]).format(dateFormat.customLabel))
        if (d) {
          groupedMonths = filterDateByWeekWithStartEnd(weeklyGroupedValues, (d as any).start, (d as any).end)
        } else {
          groupedMonths = filterDateByWeek(weeklyGroupedValues, uniqueMonths[i])
        }
      }
    } else {
      const month = dayjs(uniqueMonths[i]).format('YYYY-MM')
      /*
       * The weekly tooltip indexes the bucket list, so positions must enumerate that same list or
       * two raw points in one week shift every later tooltip. Not-Ready stubs keep the raw points:
       * their flat line rides yValues remapped on filteredData, which the buckets do not carry.
       */
      const monthSource =
        selectedFrequency === CPIFrequency.Weekly && groupedByWeeks && dataStatus === DataStatus.Ready
          ? weeklyGroupedValues
          : filteredData
      groupedMonths = filterDateByMonth(monthSource, month)
    }

    /* A placeholder position here would desync the tooltip's positions-to-data index mapping. */
    if (!groupedMonths.length) continue

    const yScaleEach = d3
      .scaleLinear()
      .domain(yAxisDomain)
      .nice()
      .range([height, chartHeightStart ?? 0])
    const resolveX =
      dataStatus === DataStatus.Ready
        ? buildDateAnchoredX({
            stripeKey: uniqueMonths[i],
            stripeWidthStart,
            stripeWidth,
            selectedTimePeriod,
            selectedFrequency,
            weeklyBuckets: weeklyGroupedValues
          })
        : buildStubBandX({
            groupedMonths,
            stripeWidthStart,
            stripeWidth,
            selectedTimePeriod,
            selectedFrequency,
            isFirstStripe: i === 0
          })

    groupedMonths.forEach((each: ChartDataValue, index: number) => {
      const d =
        typeof each.xValue !== 'string'
          ? dayjs(each.xValue).format('YYYY-MM-DD')
          : each.xValue.slice(0, 10) || dayjs(each.xValue).format('YYYY-MM-DD')
      const position = {
        x: resolveX(each),
        y: yScaleEach(each.yValue),
        value: each.yValue,
        monthIndex: i,
        date: dayjs(d).format('YYYY-MM-DD')
      }
      const startString = getLineCommand({
        isFirstPoint: i === 0 && index === 0,
        prevDate: prevPointDate,
        currDate: position.date,
        frequency: selectedFrequency,
        gapsEnabled: dataStatus === DataStatus.Ready
      })

      if (startString === 'M') {
        if (openSubpathStart) isolatedPositions.push(openSubpathStart)
        openSubpathStart = position
      } else {
        openSubpathStart = null
      }

      positions.push(position)
      prevPointDate = position.date
      pathStr += `${startString}${position.x} ${position.y}`
    })
  }

  if (openSubpathStart) isolatedPositions.push(openSubpathStart)

  return [pathStr, positions, isolatedPositions]
}

export const getPathAndPositionsPredictions = ({
  uniqueMonths,
  stripeWidth,
  height,
  filteredData,
  groupedByWeeks,
  selectedTimePeriod,
  selectedFrequency,
  yAxisDomain,
  dataStatus,
  chartHeightStart,
  predictionCalculation = false
}: {
  uniqueMonths: string[]
  stripeWidth: number
  height: number
  filteredData: ChartDataValue[]
  groupedByWeeks: ChartDataExtended | null
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  yAxisDomain: [number, number]
  dataStatus: DataStatus
  chartHeightStart?: number
  predictionCalculation?: boolean
}): [string, Array<D3Position>, string] => {
  let pathStr = ``
  let pathStrPrediction = ``
  const positions: D3Position[] = []
  let weeklyGroupedValues: ChartDataValueExtended[] = []
  if (groupedByWeeks) {
    weeklyGroupedValues = groupedByWeeks.values
  }
  const lastValue = filteredData[filteredData.length - 1].xValue

  for (let i = 0; i < uniqueMonths.length; i++) {
    const stripeWidthStart = stripeWidth * i

    let groupedMonths: ChartDataValue[] = []
    if (dataStatus === DataStatus.Initializing) {
      groupedMonths = filteredData
    } else if (selectedTimePeriod === CPITimePeriod.Week) {
      const currentDate = getUtcDateAndTime(uniqueMonths[i])
      groupedMonths = filterDateByDay(filteredData, currentDate.utcFormattedDate)
    } else if (selectedTimePeriod === CPITimePeriod.Month && groupedByWeeks) {
      if (selectedFrequency === CPIFrequency.Daily) {
        const preparedData = addWeekBreakpoints(filteredData)
        const d = preparedData.find((n) => n.start === dayjs(uniqueMonths[i]).format(dateFormat.customLabel))

        if (d) {
          groupedMonths = filterDateByWeekWithStartEnd(filteredData, (d as any).start, (d as any).end)
        } else {
          groupedMonths = filterDateByWeek(filteredData, uniqueMonths[i])
        }
      } else {
        const preparedData = addWeekBreakpoints(weeklyGroupedValues)
        const d = preparedData.find((n) => n.start === dayjs(uniqueMonths[i]).format(dateFormat.customLabel))
        if (d) {
          groupedMonths = filterDateByWeekWithStartEnd(weeklyGroupedValues, (d as any).start, (d as any).end)
        } else {
          groupedMonths = filterDateByWeek(weeklyGroupedValues, uniqueMonths[i])
        }
      }
    } else {
      const month = dayjs(uniqueMonths[i]).format('YYYY-MM')
      groupedMonths = filterDateByMonth(filteredData, month)
    }

    if (!groupedMonths.length) {
      if (!predictionCalculation) {
        positions.push({
          x: 0,
          y: 0,
          value: 0
        })
      }
      pathStr += `M0 ${height}`
      continue
    }

    const stripeFraction =
      dataStatus === DataStatus.Ready && i === uniqueMonths.length - 1
        ? getLastStripeFraction({
            lastValue,
            pointsInLastStripe: groupedMonths.length,
            selectedTimePeriod,
            selectedFrequency
          })
        : 1

    const xScaleEach = d3
      .scaleBand()
      .domain(groupedMonths.map((d: ChartDataValue) => d.xValue))
      .range([stripeWidthStart, stripeWidthStart + stripeWidth * stripeFraction])
      .padding(0)
    const stripeMiddle = dataStatus !== DataStatus.Ready && i === 0 ? 0 : xScaleEach.bandwidth() / 2
    const yScaleEach = d3
      .scaleLinear()
      .domain(yAxisDomain)
      .nice()
      .range([height, chartHeightStart ?? 0])

    groupedMonths.forEach((each: ChartDataValue, index: number) => {
      let startString = 'L'
      let pathStrPredictionStart = 'L'
      if (i === 0 && index === 0) {
        startString = 'M'
        pathStrPredictionStart = 'M'
      }
      const add = dataStatus === DataStatus.Ready ? stripeMiddle : stripeMiddle * 2
      const d =
        typeof each.xValue !== 'string'
          ? dayjs(each.xValue).format('YYYY-MM-DD')
          : each.xValue.slice(0, 10) || dayjs(each.xValue).format('YYYY-MM-DD')
      const position = {
        x: (xScaleEach(each.xValue) || 0) + add,
        y: yScaleEach(each.yValue),
        value: each.yValue,
        monthIndex: i,
        date: dayjs(d).format('YYYY-MM-DD'),
        prediction: (each as any).prediction
      }
      positions.push(position)
      pathStr += `${startString}${position.x} ${position.y}`
      if ((each as any).prediction) {
        pathStrPrediction += `${pathStrPredictionStart}${position.x} ${position.y}`
      }
    })
  }

  return [pathStr, positions, pathStrPrediction]
}

export const getTodayTimeUTC = () => {
  const today = new Date()
  const offset = dayjs(today).format('Z')
  const offsetDirection = offset[0] === '-' ? 'negative' : 'positive'
  const offsetValue = parseInt(offset.slice(1, 3), 10)

  if (offsetValue !== 0) {
    if (offsetDirection === 'negative') {
      today.setHours(today.getHours() + offsetValue)
    } else {
      today.setHours(today.getHours() - offsetValue)
    }
  }

  return today.getTime()
}

export const getRangeFromToFormatted = (
  selectedFrequency: CPIFrequency,
  dataStartsAt: string,
  start: string,
  end: string,
  withYear = true
) => {
  const dataStartTime = new Date(dataStartsAt).getTime()
  const currentStartTime = new Date(start).getTime()
  const currentEndTime = new Date(end).getTime()
  const utcDateStart = getUtcDateAndTime(start)
  const utcDateEnd = getUtcDateAndTime(end)
  const rangeFrom = utcDateStart.labelFormatDate
  const rangeTo = utcDateEnd.labelFormatDate
  const rangeFromMonth = utcDateStart.month
  const rangeFromYear = utcDateStart.year
  const rangeToMonth = utcDateEnd.month
  const rangeToYear = utcDateEnd.year
  const isSameYear = rangeFromYear === rangeToYear
  const isSameMonth = rangeFromMonth === rangeToMonth
  let rangeFromFormatted = dayjs(rangeFrom).format(`MMM D${!isSameYear ? ', YYYY' : ''}`)
  let rangeToFormatted = dayjs(rangeTo).format(`${!isSameMonth ? 'MMM ' : ''}D${!isSameYear ? ', YYYY' : ''}`)
  const todayTime = getTodayTimeUTC()
  const endDayTime = new Date(rangeTo).getTime()

  /* Don't include year in format, only months and days */
  if (!withYear) {
    const rangeToFormat = rangeFromMonth === rangeToMonth ? 'D' : 'MMM D'
    rangeFromFormatted = dayjs(rangeFrom).format('MMM D')
    rangeToFormatted = dayjs(rangeTo).format(rangeToFormat)

    if (currentStartTime < dataStartTime && selectedFrequency === CPIFrequency.Daily) {
      rangeFromFormatted = dayjs(dataStartsAt).format('MMM D')
    }

    if (todayTime < endDayTime) {
      rangeToFormatted = dayjs(todayTime).format(rangeToFormat)
    }
    const today = new Date()
    const todayDate = today.getUTCDate()
    const todayMonth = today.getUTCMonth() + 1
    const todayYear = today.getUTCFullYear()
    if (utcDateStart.day === todayDate && todayMonth === utcDateStart.month && todayYear === utcDateStart.year) {
      rangeToFormatted = ''
    }

    return {
      rangeFromFormatted,
      rangeToFormatted
    }
  }

  if (currentStartTime < dataStartTime && selectedFrequency === CPIFrequency.Daily) {
    rangeFromFormatted = dayjs(dataStartsAt).format('MMM D')
  }
  if (currentEndTime > todayTime) {
    rangeToFormatted = dayjs().format(`${!isSameMonth ? 'MMM ' : ''}D${!isSameYear ? ', YYYY' : ''}`)
  }
  if (rangeFromFormatted.slice(-2) === rangeToFormatted) {
    rangeToFormatted = ''
  }

  return {
    rangeFromFormatted,
    rangeToFormatted
  }
}

/*
 * Days from a bucket's Monday start to its key: -1 for dense daily (a Sunday keys the following
 * week's bucket), the reporting weekday for a weekly response. The first bucket is keyed by whatever
 * day the data begins, so the mode is taken over the rest, falling back to the dense -1.
 */
const weekKeyOffset = (buckets: ChartDataValue[]): number => {
  const counts = new Map<number, number>()
  buckets.slice(1).forEach((b) => {
    const k = utcDayDiff((b as any).start.slice(0, 10), b.xValue.slice(0, 10))
    counts.set(k, (counts.get(k) ?? 0) + 1)
  })

  let offset = -1
  let best = 0
  counts.forEach((n, k) => {
    if (n > best) {
      best = n
      offset = k
    }
  })

  return offset
}

/*
 * The Monday-start weeks whose bucket key lands in the month. Observed buckets place their own week;
 * skipped weeks are placed by the series' key offset, so a sparsely filled month spans the same grid
 * as its populated neighbours. Membership is monotone over the grid, so months always tile.
 *
 * With selectedTimePeriod, the stripe holding the window's first day opens on that day's week -
 * weeks before it sit outside the period, not in a data gap. The point-position slot grid omits
 * the param, so a clamped label never moves points.
 */
const monthWeekSpan = (
  monthKey: string,
  buckets: ChartDataValue[] = [],
  selectedTimePeriod?: CPITimePeriod
): [string, string] => {
  const key = monthKey.slice(0, 7)
  const [year, month] = key.split('-').map(Number)
  const monthEnd = `${key}-${pad2(numDays(year, month))}`
  const offset = weekKeyOffset(buckets)
  const keyByStart = new Map(buckets.map((b) => [(b as any).start.slice(0, 10), b.xValue.slice(0, 10)]))

  const owned: string[] = []
  const last = addUtcDays(getWeekStartUTC(monthEnd), 14)
  for (let s = addUtcDays(getWeekStartUTC(`${key}-01`), -14); s <= last; s = addUtcDays(s, 7)) {
    const bucketKey = keyByStart.get(s) ?? addUtcDays(s, offset)
    if (bucketKey.slice(0, 7) === key) owned.push(s)
  }

  let start = owned[0]
  const end = addUtcDays(owned[owned.length - 1], 6)
  if (selectedTimePeriod) {
    const windowStart = windowStartISO(selectedTimePeriod, new Date().toISOString().slice(0, 10))
    const firstWindowWeek = windowStart && getWeekStartUTC(windowStart)
    if (firstWindowWeek && start < firstWindowWeek && firstWindowWeek <= end) {
      start = firstWindowWeek
    }
  }

  return [start, end]
}

/*
 * The span a 3M or 6M weekly stripe stands for, clamped to the selected window and to today, as its
 * label states it. Returns no end once the today clamp would land before the span opens, which it
 * does in the first days of a month whose 1st is not a Monday.
 * */
const monthWeekSpanToDate = (
  stripeKey: string,
  buckets: ChartDataValue[],
  selectedTimePeriod: CPITimePeriod
): { start: string; end: string } => {
  const [start, monthEnd] = monthWeekSpan(stripeKey, buckets, selectedTimePeriod)
  /* UTC, like monthWeekSpan itself: a local date can be a day ahead and land in the next month. */
  const todayISO = new Date().toISOString().slice(0, 10)
  let end = monthEnd > todayISO ? todayISO : monthEnd

  /*
   * The current month ends at its last reported week, not at today. With no buckets of its own it
   * keeps the today clamp: an earlier month's bucket end would invert the range.
   */
  if (stripeKey.slice(0, 7) === todayISO.slice(0, 7)) {
    const lastOwnEnd = buckets
      .filter((b) => b.xValue.slice(0, 7) === todayISO.slice(0, 7))
      .map((b) => (b as any).end.slice(0, 10))
      .sort()
      .pop()
    if (lastOwnEnd && lastOwnEnd < end) end = lastOwnEnd
  }

  return { start, end: end < start ? '' : end }
}

export const formatXLabel = ({
  date,
  selectedTimePeriod,
  selectedFrequency,
  filteredData,
  dataLastValue
}: {
  date: string
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  filteredData: ChartDataValue[]
  dataLastValue?: any
}) => {
  if (selectedTimePeriod === CPITimePeriod.Week) {
    return dayjs(date).format('MMM D')
  }
  if (selectedTimePeriod === CPITimePeriod.Month) {
    const stripeStart = dayjs(date).format(dateFormat.customLabel)
    const d = filteredData.find((n: any) => n.xValue.slice(0, 10) === stripeStart)

    /* A stripe the response holds no points for still spans its own week, like the stripes beside it. */
    const start = d ? (d as any).start : stripeStart
    let end = d ? (d as any).end : addUtcDays(stripeStart, 6)

    // For daily frequency, cap the last stripe's end to the last actual data point
    // rather than today, since each day is a distinct data point
    if (selectedFrequency === CPIFrequency.Daily && dataLastValue?.xValue) {
      /* UTC, like the dates it is compared against; a local date can be a day ahead of them. */
      const today = new Date().toISOString().slice(0, 10)
      if (end >= today) {
        const lastDataDate = (
          typeof dataLastValue.xValue === 'string' ? dataLastValue.xValue : new Date(dataLastValue.xValue).toISOString()
        ).slice(0, 10)
        if (lastDataDate < end) {
          end = lastDataDate
        }
      }
    }

    /* Only the window's first day pulls a daily stripe's start forward; the first day of data never does. */
    const windowStart = windowStartISO(selectedTimePeriod, new Date().toISOString().slice(0, 10))
    const startsAt = start < windowStart && windowStart <= end ? windowStart : start

    const { rangeFromFormatted, rangeToFormatted } = getRangeFromToFormatted(
      selectedFrequency,
      startsAt,
      start,
      end,
      false
    )

    if (!rangeToFormatted) return rangeFromFormatted

    return rangeFromFormatted === rangeToFormatted ? rangeFromFormatted : `${rangeFromFormatted}-${rangeToFormatted}`
  }
  if (selectedTimePeriod === CPITimePeriod.ThreeMonths || selectedTimePeriod === CPITimePeriod.SixMonths) {
    if (selectedFrequency === CPIFrequency.Weekly) {
      /*
       * monthWeekSpanToDate clamps the current month back to today itself: the clamp in
       * getRangeFromToFormatted does not narrow the month, and a range ending in the next month
       * spells that month out.
       * */
      const { start, end } = monthWeekSpanToDate(date, filteredData, selectedTimePeriod)

      // A December stripe can end in January; keep its label year-free like its neighbours.
      const { rangeFromFormatted, rangeToFormatted } = getRangeFromToFormatted(
        selectedFrequency,
        start,
        start,
        end,
        false
      )

      /* Nothing closes the range; the 1M branch takes the same way out for a stripe starting today. */
      if (!rangeToFormatted || !end) return rangeFromFormatted

      return `${rangeFromFormatted}-${rangeToFormatted}`
    }

    /*
     * This used to be range from-to, but changed because:
     * ONY-2585 */
    return dayjs(date).format("MMM 'YY")
  }
  if (selectedTimePeriod === CPITimePeriod.PastYear) {
    return dayjs(date).format("MMM 'YY")
  }
  if (selectedTimePeriod === CPITimePeriod.Custom) {
    return dayjs(date).format("MMM 'YY")
  }

  return ''
}

/*
 * The stripe holding an x measured in the svg's own coordinate system. Clamped, so the far edge of
 * the last stripe answers with that stripe instead of falling off the axis.
 * */
const findStripeIndex = (numberOfStripes: number, stripeWidth: number, x: number) => {
  if (numberOfStripes < 1 || stripeWidth <= 0) return -1

  return Math.min(Math.max(Math.floor(x / stripeWidth), 0), numberOfStripes - 1)
}

/*
 * Receive the end date of date range and return either the received date if its before today,
 * or today if end date is after today
 * */
export const getEndDateOrToday = (suggestedEndDate: string) => {
  const suggestedEndTime = new Date(suggestedEndDate).getTime()
  const today = new Date().getTime()

  return suggestedEndTime > today ? new Date().toISOString() : suggestedEndDate
}

/*
 * If the suggested start date is before the dataset start date, return the dataset start date
 * */
export const getStartDateOrFirstDataDate = (suggestedStartDate: string, dataStartsAt: string) => {
  const suggestedStart = new Date(suggestedStartDate)
  const dataStartDate = new Date(dataStartsAt)

  return suggestedStart < dataStartDate ? dataStartsAt : suggestedStartDate
}

/*
 * A stripe padded out to cover the selected period holds no data, so its range comes from the
 * stripe key itself. Labels stay addable on days the source never reported.
 */
const getStripeRangeFromKey = (
  stripeKey: string,
  selectedTimePeriod: CPITimePeriod
): { start: string; end: string } => {
  if (selectedTimePeriod === CPITimePeriod.Week) {
    return { start: dayjs(stripeKey).format(dateFormat.customLabel), end: '' }
  }

  const stripeStart = getStripeStartDate(stripeKey, selectedTimePeriod)
  const stripeEnd =
    selectedTimePeriod === CPITimePeriod.Month
      ? addUtcDays(stripeStart, 6)
      : dayjs(stripeStart).endOf('month').format(dateFormat.customLabel)

  return { start: stripeStart, end: getUtcDateAndTime(getEndDateOrToday(stripeEnd)).labelFormatDate }
}

/*
 * The range a click on a stripe reports, from the stripe's own key. Identifying the stripe by key
 * rather than by where the pointer landed keeps the answer independent of the viewport: a mouse x
 * has to be corrected for the chart's position on the page, and any error there lands the click on
 * a neighbouring stripe or, at the end of the axis, on none at all.
 * */
export const getCustomDatesForStripe = (
  date: string,
  selectedTimePeriod: CPITimePeriod,
  selectedFrequency: CPIFrequency,
  filteredData: any[],
  dataStartsAt: string
): {
  date: string
  start: string
  end: string
} => {
  let start = ''
  let end = ''
  if (!date) return { date: '', start: '', end: '' }

  if (selectedTimePeriod === CPITimePeriod.Week) {
    start = dayjs(date).format(dateFormat.customLabel)
    end = ''
  }
  if (selectedTimePeriod === CPITimePeriod.Month) {
    const groupedDays = filterDateByWeek(filteredData, date)
    if (!groupedDays.length) return { date, ...getStripeRangeFromKey(date, selectedTimePeriod) }
    const startDate = getStartDateOrFirstDataDate((groupedDays as any)[0].start, dataStartsAt)
    const endDate = getEndDateOrToday((groupedDays as any)[0].end)
    start = dayjs(startDate).format(dateFormat.customLabel)
    end = dayjs(endDate).format(dateFormat.customLabel)
  }
  if ([CPITimePeriod.ThreeMonths, CPITimePeriod.SixMonths, CPITimePeriod.PastYear].includes(selectedTimePeriod)) {
    /*
     * A 3M or 6M weekly stripe is labelled with the weeks its month owns, so that is the range a
     * click on it reports - not the subset of those weeks the response happened to fill.
     * */
    if (selectedFrequency === CPIFrequency.Weekly && selectedTimePeriod !== CPITimePeriod.PastYear) {
      return { date, ...monthWeekSpanToDate(date, filteredData, selectedTimePeriod) }
    }

    const groupedDays = filterDateByMonth(filteredData, date)
    if (!groupedDays.length) return { date, ...getStripeRangeFromKey(date, selectedTimePeriod) }
    if (selectedFrequency === CPIFrequency.Weekly) {
      const startDate = (groupedDays as any)[0].start
      const endDate = getEndDateOrToday((groupedDays as any)[groupedDays.length - 1].end)
      start = dayjs(startDate).format(dateFormat.customLabel)
      end = dayjs(endDate).format(dateFormat.customLabel)
    } else if (selectedFrequency === CPIFrequency.Monthly) {
      const { year, month } = getUtcDateAndTime(groupedDays[0].xValue)
      const monthFormatted = month < 10 ? `0${month}` : month
      const firstDayOfMonth = '01'
      const lastDayOfMonth = dayjs(groupedDays[0].xValue).daysInMonth()
      const endDayParsed = getUtcDateAndTime(`${year}-${monthFormatted}-${lastDayOfMonth}`).labelFormatDate
      const endDate = getEndDateOrToday(endDayParsed)
      start = `${year}-${monthFormatted}-${firstDayOfMonth}`
      end = getUtcDateAndTime(endDate).labelFormatDate
    } else {
      start = getUtcDateAndTime(groupedDays[0].xValue).labelFormatDate
      end = getUtcDateAndTime(groupedDays[groupedDays.length - 1].xValue).labelFormatDate
    }
  }
  if (selectedTimePeriod === CPITimePeriod.Custom) {
    // Handle Custom time period
    const groupedDays = filterDateByMonth(filteredData, date)

    if (selectedFrequency === CPIFrequency.Weekly && groupedDays.length > 0) {
      // For weekly frequency, use the start/end range of the week data
      const startDate = (groupedDays as any)[0].start
      const endDate = getEndDateOrToday((groupedDays as any)[groupedDays.length - 1].end)
      start = dayjs(startDate).format(dateFormat.customLabel)
      end = dayjs(endDate).format(dateFormat.customLabel)
    } else if (selectedFrequency === CPIFrequency.Monthly && groupedDays.length > 0) {
      // For monthly frequency, use the entire month range
      const { year, month } = getUtcDateAndTime(groupedDays[0].xValue)
      const monthFormatted = month < 10 ? `0${month}` : month
      const firstDayOfMonth = '01'
      const lastDayOfMonth = dayjs(groupedDays[0].xValue).daysInMonth()
      const endDayParsed = getUtcDateAndTime(`${year}-${monthFormatted}-${lastDayOfMonth}`).labelFormatDate
      const endDate = getEndDateOrToday(endDayParsed)
      start = `${year}-${monthFormatted}-${firstDayOfMonth}`
      end = getUtcDateAndTime(endDate).labelFormatDate
    } else if (selectedFrequency === CPIFrequency.Daily && groupedDays.length > 0) {
      // For daily frequency, use the range of days in that month
      start = getUtcDateAndTime(groupedDays[0].xValue).labelFormatDate
      end = getUtcDateAndTime(groupedDays[groupedDays.length - 1].xValue).labelFormatDate
    } else {
      // Fallback: try to extract month range from the date
      if (date.includes('-')) {
        const [year, month] = date.split('-')
        const firstDayOfMonth = `${year}-${month}-01`
        const lastDayOfMonth = dayjs(firstDayOfMonth).endOf('month').format(dateFormat.customLabel)
        const endDate = getEndDateOrToday(lastDayOfMonth)
        start = firstDayOfMonth
        end = endDate
      } else {
        // Single day fallback
        start = dayjs(date).format(dateFormat.customLabel)
        end = ''
      }
    }
  }

  return { date, start, end }
}

/*
 * The range for the stripe under an x measured in the svg's own coordinate system. Only the floating
 * add-label button needs this - it follows the cursor, so it has no stripe of its own to name.
 * */
export const getCustomDatesForTimePeriod = (
  x: number,
  stripeWidth: number,
  selectedTimePeriod: CPITimePeriod,
  selectedFrequency: CPIFrequency,
  uniqueMonths: string[],
  filteredData: any[],
  dataStartsAt: string
): {
  date: string
  start: string
  end: string
} =>
  getCustomDatesForStripe(
    uniqueMonths[findStripeIndex(uniqueMonths.length, stripeWidth, x)],
    selectedTimePeriod,
    selectedFrequency,
    filteredData,
    dataStartsAt
  )

/*
 * The period a stripe stands for. The caller fills this range into single days and counts the labels
 * that land in them to badge the stripe, so it must not depend on which of those days the response
 * reported: a label on a day with no data still belongs to the stripe covering it, and an axis padded
 * out to the selected period is largely made of such days. Not capped at today either - a label can
 * be dated ahead, and the stripe it belongs to is the one whose period contains it.
 * */
export const findDateRangeForDate = (
  date: string,
  timePeriod: CPITimePeriod,
  frequency: CPIFrequency,
  filteredData: ChartDataValue[]
): {
  date: string
  start: string
  end: string
} => {
  if (timePeriod === CPITimePeriod.Week) {
    return { date, start: date.slice(0, 10), end: '' }
  }
  if (timePeriod === CPITimePeriod.Month) {
    const start = date.slice(0, 10)

    return { date, start, end: addUtcDays(start, 6) }
  }
  if (
    frequency === CPIFrequency.Weekly &&
    (timePeriod === CPITimePeriod.ThreeMonths || timePeriod === CPITimePeriod.SixMonths)
  ) {
    const [start, end] = monthWeekSpan(date, filteredData, timePeriod)

    return { date, start, end }
  }

  const start = `${date.slice(0, 7)}-01`

  return { date, start, end: dayjs(start).endOf('month').format(dateFormat.customLabel) }
}

/*
 * The span of the whole rendered axis, from the range of its first stripe to the range of its last.
 * The labels drawer filters its list by this while each stripe badges the labels its own range holds,
 * so both have to be read off the stripes: ending it on the last day the source reported, or on the
 * day the last week opens, drops the labels sitting in the rest of the final stripe - the badge
 * counts them, the list then hides them behind "No labels in this time period".
 * */
export const getAxisDateRange = (
  uniqueMonths: string[],
  timePeriod: CPITimePeriod,
  frequency: CPIFrequency,
  filteredData: ChartDataValue[]
): DateRangeI => {
  if (!uniqueMonths.length) return { minDate: '', maxDate: '' }

  const first = findDateRangeForDate(uniqueMonths[0], timePeriod, frequency, filteredData)
  const last = findDateRangeForDate(uniqueMonths[uniqueMonths.length - 1], timePeriod, frequency, filteredData)

  return { minDate: first.start, maxDate: last.end || last.start }
}

const findOverlappingLabel = (visibleLabels: VisibleLabel[], slaYPosition: number) => {
  let hasLabelOnStart = false
  let activeLabelIndex = 0
  const shortHeight = 98
  const longHeight = 102
  const overlappingLabels: VisibleLabel[] = []
  const slaYPositionStart = slaYPosition - 28
  const slaYPositionEnd = slaYPosition + 28

  visibleLabels.forEach((label, index) => {
    const y = index + 1
    const yPosition = y === 1 ? 101.4 * 3 + 30 : y === 2 ? 101.4 * 2 + 25 : y === 3 ? 101.4 + 20 : 9
    const cardHeight = label.isShortLabel ? shortHeight : longHeight
    const cardWithHeight = yPosition + cardHeight

    if (slaYPosition > yPosition && slaYPosition < cardWithHeight && label.x < 400) {
      activeLabelIndex = index
      hasLabelOnStart = true
      overlappingLabels.push(label)
    } else if (slaYPositionEnd > yPosition && slaYPositionEnd < cardWithHeight && label.x < 400) {
      overlappingLabels.push(label)
      activeLabelIndex = index
    } else if (slaYPositionStart > yPosition && slaYPositionStart < cardWithHeight && label.x < 400) {
      overlappingLabels.push(label)
      activeLabelIndex = index
    }
  })

  if (overlappingLabels.length > 0) {
    const rightMostLabel = Math.max(...overlappingLabels.map((o) => o.x))
    const label = overlappingLabels.find((n) => n.x === rightMostLabel)

    return label as VisibleLabel
  }
  return hasLabelOnStart ? visibleLabels[activeLabelIndex] : false
}

export const getSlaGroupProps = (
  height: number,
  healthBorder: number,
  visibleLabels: VisibleLabel[],
  slaRectWidth: number
) => {
  const slaRectHeight = 56
  let slaMarginLeft = 55

  const overflowValuePadding = 8
  const maxHighValue = height - slaRectHeight / 2
  const maxLowValue = slaRectHeight / 2
  const slaYPositionValue = healthBorder
  const slaYPosition =
    slaYPositionValue >= maxHighValue
      ? maxHighValue - overflowValuePadding
      : slaYPositionValue <= maxLowValue
      ? maxLowValue + overflowValuePadding
      : slaYPositionValue

  const activeLabel = findOverlappingLabel(visibleLabels, slaYPosition)
  if (activeLabel) {
    const { x, isShortLabel } = activeLabel
    slaMarginLeft = x < 150 ? (isShortLabel ? 150 : 280) : x + (isShortLabel ? 94 : 133)
  }

  return {
    slaYPosition,
    slaYPositionValue,
    overflowValuePadding,
    slaMarginLeft,
    slaRectHeight,
    slaRectWidth
  }
}

export const getSingularSectionsName = (sectionsName: string) => {
  let result = ''
  switch (sectionsName) {
    case 'severities':
      result = 'Severity'
      break
    case 'devices':
      result = 'Device'
      break
    case 'security_categories':
      result = 'Security Categories'
      break
    default:
      result = sectionsName
      break
  }
  return result
}

interface DummyObjectBenchmarking {
  date: string
  start: string
  end: string
  less: number
  equal: number
  more: number
  dummy: boolean
}

interface DummyObject {
  xValue: string
  yValue: number
  numerator: number
  denominator: number
  dummy: boolean
}

interface DummyObjectBenchmarking {
  date: string
  less: number
  more: number
  equal: number
  dummy: boolean
}

function generateDatesArray(startDate: Date, endDate: Date): DummyObject[] {
  const datesArray: DummyObject[] = []
  const currentDate: Date = new Date(startDate)

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= endDate) {
    datesArray.push({
      xValue: new Date(currentDate).toISOString(),
      yValue: 0,
      numerator: 0,
      denominator: 0,
      dummy: true
    })
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return datesArray
}

function generateStringDatesArrayBenchmarking(
  startDate: string | Date,
  endDate: string | Date
): DummyObjectBenchmarking[] {
  const datesArray: DummyObjectBenchmarking[] = []
  const currentDate: Date = new Date(startDate)
  const endDay = new Date(endDate)

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= endDay) {
    datesArray.push({
      date: new Date(currentDate).toISOString(),
      start: new Date(currentDate).toISOString(),
      end: '',
      less: 0,
      equal: 0,
      more: 0,
      dummy: true
    })
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return datesArray.map((n) => {
    return {
      ...n,
      date: dayjs(n.date).format(dateFormat.customLabel),
      start: dayjs(n.start).format(dateFormat.customLabel)
    }
  })
}

export function generateStringDatesArray(startDate: string, endDate: string): DummyObject[] {
  const datesArray: DummyObject[] = []
  const currentDate: Date = new Date(startDate)
  const endDay = new Date(endDate)

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= endDay) {
    datesArray.push({
      xValue: new Date(currentDate).toISOString(),
      yValue: 0,
      numerator: 0,
      denominator: 0,
      dummy: true
    })
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return datesArray
}

export function fillDatesBetweenRangeStrings(startDate: string, end: string): string[] {
  const datesArray: string[] = []
  const currentDate: Date = new Date(startDate)
  const endDate = new Date(end)

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= endDate) {
    datesArray.push(new Date(currentDate).toISOString())
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return datesArray
}

export function fillDatesBetweenRange(startDate: Date, endDate: Date): string[] {
  const datesArray: string[] = []
  const currentDate: Date = new Date(startDate)

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= endDate) {
    datesArray.push(new Date(currentDate).toISOString())
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return datesArray
}

export const calculateLabelCount = (
  labelsCounts: LabelCount[],
  timePeriod: CPITimePeriod,
  customPeriod: string
): number => {
  if (!labelsCounts || labelsCounts.length === 0) return 0

  let startDate: dayjs.Dayjs
  let endDate: dayjs.Dayjs

  if (timePeriod === CPITimePeriod.Custom && customPeriod) {
    const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const parts = customPeriod.split('-')
    const startPart = parts[0].trim().split(" '")
    const endPart = parts[1].trim().split(" '")

    const startMonth = shortMonths.findIndex((month) => month === startPart[0]) + 1
    const startYear = startPart.length > 1 ? parseInt(`20${startPart[1]}`) : new Date().getFullYear()
    const endMonth = shortMonths.findIndex((month) => month === endPart[0]) + 1
    const endYear = parseInt(`20${endPart[1]}`)

    startDate = dayjs(`${startYear}-${String(startMonth).padStart(2, '0')}-01`)
    endDate = dayjs(`${endYear}-${String(endMonth).padStart(2, '0')}-01`).endOf('month')
  } else {
    endDate = dayjs()

    switch (timePeriod) {
      case CPITimePeriod.Week:
        startDate = dayjs().subtract(1, 'week')
        break
      case CPITimePeriod.Month:
        startDate = dayjs().subtract(1, 'month')
        break
      case CPITimePeriod.ThreeMonths:
        startDate = dayjs().subtract(3, 'months')
        break
      case CPITimePeriod.SixMonths:
        startDate = dayjs().subtract(6, 'months')
        break
      case CPITimePeriod.PastYear:
        startDate = dayjs().subtract(1, 'year')
        break
      default:
        return labelsCounts.reduce((total, item) => total + item.count, 0)
    }
  }

  return labelsCounts
    .filter((item) => {
      const itemDate = dayjs(item.date)
      // Use >= and <= comparisons instead of isSameOrBefore/isSameOrAfter
      return itemDate.valueOf() >= startDate.valueOf() && itemDate.valueOf() <= endDate.valueOf()
    })
    .reduce((total, item) => total + item.count, 0)
}

export const getSelectedPeriodNumber = (timePeriod: CPITimePeriod): string => {
  if (timePeriod === CPITimePeriod.Week) {
    return '4'
  }
  if (timePeriod === CPITimePeriod.PastYear) return '12'

  return timePeriod.replace('M', '')
}

export const fillBlankData = (
  values: ChartDataValue[] | HistoryPerformanceDataExtended[],
  selectedTimePeriod: CPITimePeriod
): ChartDataValue[] | HistoryPerformanceDataExtended[] => {
  const [initialValue, finalValue] = [values[0], values.slice(-1)[0]]
  const date = new Date(initialValue.xValue)
  date.setDate(date.getDate() - 1)
  const subtractedDate = date
  const [initialMonth, finalMonth] = [new Date(subtractedDate).getMonth(), new Date(finalValue.xValue).getMonth()]
  const fMonth = finalMonth === 0 ? 12 : finalMonth
  const selectedMonths = parseInt(getSelectedPeriodNumber(selectedTimePeriod))
  const monthDiff = initialMonth === finalMonth ? selectedMonths : fMonth - initialMonth
  let dummyArray: DummyObject[] = []

  if (selectedMonths >= monthDiff) {
    const fakeMonthsGap = selectedMonths === monthDiff ? selectedMonths - 1 : selectedMonths - monthDiff
    const initDate = new Date(initialValue.xValue)
    if (selectedTimePeriod === CPITimePeriod.Month) {
      const existingStripes = values.length
      const stripesGap = MAX_STRIPES_HISTORY_CHARTS - existingStripes
      if (stripesGap <= 0) return values
      const startValue = values[0].xValue
      let start = new Date(startValue)

      const dummiesArray: any[] = []
      Array.from(Array(stripesGap).keys()).forEach(() => {
        const prevWeekDay = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000)

        const startOfWeekDate = startOfWeek(prevWeekDay, { weekStartsOn: 1 })
        const startDay = startOfWeekDate.getDate()
        const startMonth = startOfWeekDate.getMonth() + 1
        const startYear = startOfWeekDate.getFullYear()

        const endOfWeekDate = endOfWeek(prevWeekDay, { weekStartsOn: 1 })
        const endYear = prevWeekDay.getFullYear()
        const endDay = endOfWeekDate.getDate()
        const endMonth = endOfWeekDate.getMonth() + 1

        const formattedStart = dayjs(`${startYear}-${startMonth}-${startDay}`).format('YYYY-MM-DD')
        const formattedEnd = dayjs(`${endYear}-${endMonth}-${endDay}`).format('YYYY-MM-DD')
        const result = generateStringDatesArray(formattedStart, formattedEnd)
        if (result.length > 0) {
          const midIndex = Math.min(3, result.length - 1)
          const res = {
            ...result[midIndex],
            xValue: result[midIndex].xValue.slice(0, 10),
            start: result[0].xValue.slice(0, 10),
            end: result[result.length - 1].xValue.slice(0, 10)
          }
          dummiesArray.push(res)
        }
        start = new Date(prevWeekDay)
      })

      return [...dummiesArray, ...values].sort((a, b) =>
        new Date(b.xValue).getTime() >= new Date(a.xValue).getTime() ? -1 : 1
      )
    } else {
      const dataUntilMonths = initDate.getMonth() - fakeMonthsGap
      const startDate: Date = new Date(initDate.getFullYear(), dataUntilMonths, new Date().getDate())
      const endDate: Date = new Date(subtractedDate)
      dummyArray = generateDatesArray(startDate, endDate)
    }
  }
  ;(values as any) = [...dummyArray, ...values]

  return values
}

export const fillHistoricalBlankData = (
  values: ChartDataValue[] | HistoryPerformanceDataExtended[],
  selectedTimePeriod: CPITimePeriod,
  uniqueMonths: string[]
): ChartDataValue[] | HistoryPerformanceDataExtended[] => {
  const [initialValue, finalValue] = [values[0], values.slice(-1)[0]]
  const date = new Date(initialValue.xValue)
  date.setDate(date.getDate() - 1)
  const subtractedDate = date
  const [initialMonth, finalMonth] = [new Date(subtractedDate).getMonth(), new Date(finalValue.xValue).getMonth()]
  const fMonth = finalMonth === 0 ? 12 : finalMonth
  const selectedMonths = parseInt(getSelectedPeriodNumber(selectedTimePeriod))
  const monthDiff = initialMonth === finalMonth ? selectedMonths : fMonth - initialMonth
  let dummyArray: DummyObject[] = []

  // When data is provided for 12 months we need to make sure monthDiff > -1
  if (monthDiff >= 0 && selectedMonths >= monthDiff) {
    const initDate = new Date(initialValue.xValue)
    const fakeMonthsGap =
      selectedMonths === 3 && uniqueMonths.length === 4
        ? 0
        : selectedMonths === 6 && uniqueMonths.length === 7
        ? 0
        : selectedMonths === 12 && uniqueMonths.length === 13
        ? 0
        : selectedMonths === monthDiff
        ? selectedMonths
        : selectedMonths - monthDiff

    if (selectedTimePeriod === CPITimePeriod.Month) {
      const existingStripes = values.length
      const stripesGap = MAX_STRIPES_HISTORY_CHARTS - existingStripes
      if (stripesGap <= 0) return values
      const startValue = values[0].xValue
      let start = new Date(startValue)

      const dummiesArray: any[] = []
      Array.from(Array(stripesGap).keys()).forEach(() => {
        const prevWeekDay = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000)

        const startOfWeekDate = startOfWeek(prevWeekDay, { weekStartsOn: 1 })
        const startDay = startOfWeekDate.getDate()
        const startMonth = startOfWeekDate.getMonth() + 1
        const startYear = startOfWeekDate.getFullYear()

        const endOfWeekDate = endOfWeek(prevWeekDay, { weekStartsOn: 1 })
        const endYear = prevWeekDay.getFullYear()
        const endDay = endOfWeekDate.getDate()
        const endMonth = endOfWeekDate.getMonth() + 1

        const formattedStart = dayjs(`${startYear}-${startMonth}-${startDay}`).format('YYYY-MM-DD')
        const formattedEnd = dayjs(`${endYear}-${endMonth}-${endDay}`).format('YYYY-MM-DD')
        const result = generateStringDatesArray(formattedStart, formattedEnd)
        if (result.length > 0) {
          const midIndex = Math.min(3, result.length - 1)
          const res = {
            ...result[midIndex],
            xValue: result[midIndex].xValue.slice(0, 10),
            start: result[0].xValue.slice(0, 10),
            end: result[result.length - 1].xValue.slice(0, 10)
          }
          dummiesArray.push(res)
        }
        start = new Date(prevWeekDay)
      })

      return [...dummiesArray, ...values].sort((a, b) =>
        new Date(b.xValue).getTime() >= new Date(a.xValue).getTime() ? -1 : 1
      )
    } else {
      const dataUntilMonths = initDate.getMonth() - fakeMonthsGap
      const startDate: Date = new Date(initDate.getFullYear(), dataUntilMonths, new Date().getDate())
      const endDate: Date = new Date(subtractedDate)
      dummyArray = generateDatesArray(startDate, endDate)
    }
  }
  ;(values as any) = [...dummyArray, ...values]

  return values
}

export const fillBlankDataBenchmarking = (
  values: HistoryBenchmarkDataValues[],
  selectedTimePeriod: CPITimePeriod,
  uniqueMonths: string[]
): HistoryBenchmarkDataValues[] => {
  const [initialValue, finalValue] = [values[0], values.slice(-1)[0]]
  const date = new Date(initialValue.date)
  date.setDate(date.getDate() - 1)
  const subtractedDate = date
  const [initialMonth, finalMonth] = [new Date(subtractedDate).getMonth(), new Date(finalValue.date).getMonth()]
  const fMonth = finalMonth === 0 ? 12 : finalMonth
  const selectedMonths = parseInt(getSelectedPeriodNumber(selectedTimePeriod))
  const monthDiff =
    initialMonth === finalMonth
      ? selectedMonths
      : finalMonth < initialMonth
      ? Math.abs(finalMonth + 12 - initialMonth + 12) - 12
      : fMonth - initialMonth
  let dummyArray: DummyObjectBenchmarking[] = []

  if (selectedTimePeriod === CPITimePeriod.Month) {
    const existingStripes = values.length
    const stripesGap = MAX_STRIPES_HISTORY_CHARTS - existingStripes
    if (stripesGap <= 0) return values
    const startValue = values[0].date
    let start = new Date(startValue)

    const dummiesArray: any[] = []
    Array.from(Array(stripesGap).keys()).forEach(() => {
      const prevWeekDay = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000)

      const startOfWeekDate = startOfWeek(prevWeekDay, { weekStartsOn: 1 })
      const startDay = startOfWeekDate.getDate()
      const startMonth = startOfWeekDate.getMonth() + 1
      const startYear = startOfWeekDate.getFullYear()

      const endOfWeekDate = endOfWeek(prevWeekDay, { weekStartsOn: 1 })
      const endYear = prevWeekDay.getFullYear()
      const endDay = endOfWeekDate.getDate()
      const endMonth = endOfWeekDate.getMonth() + 1

      const formattedStart = dayjs(`${startYear}-${startMonth}-${startDay}`).format('YYYY-MM-DD')
      const formattedEnd = dayjs(`${endYear}-${endMonth}-${endDay}`).format('YYYY-MM-DD')
      const result = generateStringDatesArrayBenchmarking(formattedStart, formattedEnd)
      const res = {
        ...result[3],
        xValue: dayjs(result[3].date).format('YYYY-MM-DD'),
        start: result[0].date.slice(0, 10),
        end: result[result.length - 1].date.slice(0, 10)
      }
      dummiesArray.push(res)
      start = new Date(prevWeekDay)
    })

    return [...dummiesArray, ...values].sort((a, b) =>
      new Date(b.date).getTime() >= new Date(a.date).getTime() ? -1 : 1
    )
  }

  if (selectedMonths >= monthDiff) {
    // const fakeMonthsGap = selectedMonths === monthDiff ? selectedMonths - 1 : selectedMonths - monthDiff
    const fakeMonthsGap =
      selectedMonths === 3 && uniqueMonths.length === 4
        ? 0
        : selectedMonths === 6 && uniqueMonths.length === 7
        ? 0
        : selectedMonths === 12 && uniqueMonths.length === 13
        ? 0
        : selectedMonths === monthDiff
        ? selectedMonths - 1
        : selectedMonths - monthDiff
    const initDate = new Date(initialValue.date)
    const dataUntilMonths = initDate.getMonth() - fakeMonthsGap
    const startDate: Date = new Date(initDate.getFullYear(), dataUntilMonths, new Date().getDate())
    const endDate: Date = new Date(subtractedDate)
    dummyArray = generateStringDatesArrayBenchmarking(startDate, endDate)
    return [...dummyArray, ...values]
  }

  return values
}

export const sortLabels = (a: Label, b: Label) => {
  const isCustomLabel = a.action === CUSTOM_LABEL_ID || b.action === CUSTOM_LABEL_ID
  const dateA = isCustomLabel && a.data.date ? a.inserted_at : a.created_at
  const dateB = isCustomLabel && b.data.date ? b.inserted_at : b.created_at

  return new Date(dateB).getTime() > new Date(dateA).getTime() ? -1 : 1
}

const getLabelPadding = ({
  selectedTimePeriod,
  numberOfStripes,
  monthIndex,
  uniqueMonths
}: {
  selectedTimePeriod: CPITimePeriod
  numberOfStripes: number
  monthIndex: number
  uniqueMonths: string[]
}) => {
  if (selectedTimePeriod === CPITimePeriod.Month) {
    return numberOfStripes === 7 ? 120 : numberOfStripes === 9 ? 70 : 105
  }
  if (selectedTimePeriod === CPITimePeriod.ThreeMonths) {
    return 129 + 64
  }
  if (selectedTimePeriod === CPITimePeriod.SixMonths) {
    if (monthIndex === uniqueMonths.length - 1) {
      return 80
    }
    return 120
  }
  if (selectedTimePeriod === CPITimePeriod.PastYear) {
    if (monthIndex === uniqueMonths.length - 1) {
      return -20
    }
    if (monthIndex === 0) {
      return 90
    }
    return 60
  }
  return 0
}

export const findVisibleLabelsXPosition = (pinnedLabels: Label[], positions: D3Position[], width: number) => {
  const res = pinnedLabels
    .map((label) => {
      const isCustomLabel = label.action === CUSTOM_LABEL_ID
      const description = isCustomLabel ? label.data.description : label.description
      const date = getUtcDateAndTime(isCustomLabel ? (label.data.date as string) : label.inserted_at).labelFormatDate
      const formattedDateWeekly = dayjs(date).format(dateFormat.customLabel)
      const foundPosition = positions.find((n) => n.date && n.date === formattedDateWeekly)

      let pos = foundPosition ? foundPosition.x : -1
      if (pos > width - 129) {
        pos = pos - 54
      }

      return {
        ...label,
        x: pos,
        isShortLabel: description.length <= 25
      }
    })
    .filter((n) => n.x >= 0)
  const result = res.length > MAX_PINNED_LABELS_COUNT ? res.slice(-MAX_PINNED_LABELS_COUNT) : res

  return result.sort(sortLabels)
}

export const findVisibleLabelsXPositionMonthly = (
  pinnedLabels: Label[],
  width: number,
  uniqueMonths: string[],
  selectedTimePeriod: CPITimePeriod
) => {
  const res = pinnedLabels
    .map((label) => {
      const stripeWidth = width / uniqueMonths.length
      const numberOfStripes = width / stripeWidth
      const isCustomLabel = label.action === CUSTOM_LABEL_ID
      const description = isCustomLabel ? label.data.description : label.description
      const date = getUtcDateAndTime(isCustomLabel ? (label.data.date as string) : label.inserted_at).labelFormatDate
      const formattedDateMonthly = dayjs(date).format('YYYY-MM')
      const formattedDateWeekly = dayjs(date).format(dateFormat.customLabel)
      let monthIndex = uniqueMonths.indexOf(formattedDateMonthly)

      if (selectedTimePeriod === CPITimePeriod.Month) {
        const mappedWeeks = uniqueMonths.map((n, i) => {
          const d = new Date(uniqueMonths[i + 1])
          d.setDate(d.getDate() - 1)
          const lastDate = i + 1 >= uniqueMonths.length ? new Date() : new Date(d)

          return fillDatesBetweenRange(new Date(n), lastDate)
        })
        mappedWeeks.forEach((week, weekIndex) => {
          week.forEach((c) => {
            const formattedMappedDate = dayjs(c).format(dateFormat.customLabel)
            if (formattedDateWeekly === formattedMappedDate) {
              monthIndex = weekIndex
            }
          })
        })
      }

      const padding = getLabelPadding({
        selectedTimePeriod,
        numberOfStripes,
        monthIndex,
        uniqueMonths
      })

      return {
        ...label,
        x: (width / uniqueMonths.length) * monthIndex + padding,
        isShortLabel: description.length <= 25
      }
    })
    .filter((n) => n.x >= 0)
  const result = res.length > MAX_PINNED_LABELS_COUNT ? res.slice(-MAX_PINNED_LABELS_COUNT) : res
  return result.sort(sortLabels)
}

export const getLabelsDateRange = ({
  selectedTimePeriod,
  selectedFrequency
}: {
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
}) => {
  const currentDate = dayjs()
  let start: any = ''
  let end: any = ''

  const range = {
    minDate: '',
    maxDate: ''
  }

  if (selectedTimePeriod === CPITimePeriod.Week) {
    end = currentDate
    start = end.subtract(7, 'day')
  }
  if (selectedTimePeriod === CPITimePeriod.Month) {
    end = selectedFrequency === 'monthly' ? currentDate.startOf('month') : currentDate
    start = currentDate.subtract(1, 'month').startOf('month')
  }
  if (selectedTimePeriod === CPITimePeriod.ThreeMonths) {
    end = selectedFrequency === 'monthly' ? currentDate.endOf('week') : currentDate
    start = selectedFrequency === 'monthly' ? end.subtract(3, 'month').startOf('month') : end.subtract(3, 'month')
  }
  if (selectedTimePeriod === CPITimePeriod.SixMonths) {
    end = selectedFrequency === 'monthly' ? currentDate.endOf('week') : currentDate
    start = selectedFrequency === 'monthly' ? end.subtract(6, 'month').startOf('month') : end.subtract(6, 'month')
  }
  if (selectedTimePeriod === CPITimePeriod.PastYear) {
    end = currentDate.endOf('week')
    start =
      selectedFrequency === 'monthly'
        ? end.subtract(1, 'day').subtract(12, 'month').startOf('month')
        : end.subtract(1, 'day').subtract(12, 'month')
  }

  range.minDate = dayjs(start).format('YYYY-MM-DD')
  range.maxDate = dayjs(end).format('YYYY-MM-DD')

  return range
}

export const formatCustomTimePickerValue = (dateFrom: string, dateTo: string) => {
  const yearFrom = dateFrom.slice(0, 4)
  const yearTo = dateTo.slice(0, 4)
  let fromFormatted = dayjs(dateFrom).format('MMM')
  if (yearFrom !== yearTo) {
    fromFormatted = dayjs(dateFrom).format("MMM 'YY")
  }
  const toFormatted = dayjs(dateTo).format("MMM 'YY")

  return `${fromFormatted}-${toFormatted}`
}

export function createPerformanceScoreData(
  cpiDisplayName: string,
  date: string,
  value: number,
  cpiName: string,
  cpiTitle: string,
  section: string,
  dataSources: PerformanceScoreCategoryDetailsDataSources[],
  weight: number,
  sla: number,
  recentFinding: string,
  score: number,
  unit: string,
  status: DataStatus
): PerformanceScoreCpi {
  return {
    cpiDisplayName,
    date,
    value,
    cpiName,
    cpiTitle,
    section,
    dataSources,
    weight,
    sla,
    recentFinding,
    score,
    unit,
    status
  }
}

/*
 * The v4 shape is the one carrying no legacy recent-finding fields. Keyed off recentPoint instead,
 * a response the backend omits it from routes entirely as legacy and reads recentDate off a payload
 * that has none.
 * */
export const instanceOfSnowflake = (object: ChartData | SnowflakeChartData): object is SnowflakeChartData => {
  return !('recentDate' in object)
}

export const formDataSourceFromIntegration = (
  integrations: CPILibraryConfigurationIntegration[]
): PerformanceScoreCategoryDetailsDataSources[] => {
  return integrations.map((c) => ({
    logo_path: c.name.includes('abnormal') ? 'abnormalsecurity.png' : `${c.name}_icon.svg`,
    name: c.name,
    display_name: c.configName || '',
    integrationConfig: {
      connection_id: '',
      created_at: '',
      created_by: '',
      description: '',
      id: c.id,
      integration_name: c.instanceName,
      name: c.name,
      prefect_deployment_id: '',
      source_id: '',
      syncStatus: {
        status: IntegrationStatusMessage.success,
        errorMessage: undefined
      },
      tenant_id: '',
      updated_at: '',
      updated_by: ''
    },
    syncStatus: {
      status: IntegrationStatusMessage.success,
      errorMessage: undefined
    },
    errorMessage: ''
  }))
}

export const extractCpiFromPathname = (pathname: string): string => {
  const pathSegments = pathname.split('/')
  const cpiIndex = pathSegments.findIndex((segment) => segment === 'cpis')
  return cpiIndex !== -1 && cpiIndex + 1 < pathSegments.length ? pathSegments[cpiIndex + 1] : ''
}

export const getRecentValueDate = (chartData: ChartData | SnowflakeChartData | null) => {
  // Helper to convert YYYY-MM-DD to MM/DD/YYYY without timezone conversion
  const formatDateString = (isoDate: string) => {
    const dateStr = isoDate.slice(0, 10) // Get YYYY-MM-DD
    const [year, month, day] = dateStr.split('-')
    return `${month}/${day}/${year}`
  }

  if (!chartData) return dayjs().format(dateFormat.auditLogDefault)

  if (instanceOfSnowflake(chartData)) {
    /* Nothing recent to date; the card shows its No Data treatment, which carries no date. */
    if (!chartData.recentPoint) return ''
    const d = chartData.recentPoint.xValue
    const test = d.slice(0, 10)
    const today = new Date().toISOString().slice(0, 10)

    if (test === today) {
      const yesterday = chartData.values[chartData.values.length - 1]
      return formatDateString(yesterday.xValue)
    }
    return formatDateString(d)
  } else {
    const date = getUtcDateAndTime(chartData.recentDate).labelFormatDate
    return formatDateString(date)
  }
}

// Builds the recent-value HTML string (rounded value + unit span) shown in the
// trends "Recent Finding" box. Shared so the live display and the initial-load
// snapshot in CPIDetails stay in sync. For Snowflake data, if the recent point
// is today we fall back to yesterday's point (today is still accumulating).
export const formatRecentValueHtml = (
  chartData: ChartData | SnowflakeChartData | null,
  manifestData: CPIManifest | null
): string => {
  if (!chartData) return ''

  let computedLastValue: number
  if (instanceOfSnowflake(chartData)) {
    /* A withheld recent point means no eligible finding; the series must not stand in for it. */
    const recentPoint = chartData.recentPoint
    if (!recentPoint) return ''

    const recentDate = recentPoint.xValue.slice(0, 10)
    const todayDate = new Date().toISOString().slice(0, 10)
    computedLastValue =
      todayDate === recentDate
        ? Math.round(chartData.values[chartData.values.length - 1].yValue)
        : Math.round(recentPoint.yValue)
  } else {
    // Use the most-recent point from the (severity-scoped) values series rather than the
    // top-level recentValue, which the API does not re-scope per severity. Keeps the
    // "Recent Finding" card in sync with the chart on filter changes (ONYXIA-8156).
    const recentDate = getUtcDateAndTime(chartData.recentDate).labelFormatDate
    const recentPoint =
      chartData.values.find((v) => v.xValue.slice(0, 10) === recentDate) ??
      chartData.values[chartData.values.length - 1]
    computedLastValue = Math.round(recentPoint?.yValue ?? chartData.recentValue ?? 0)
  }

  const visualizationParam = manifestData?.ui.trend_chart.user_input.visualization_param
  const unitText = formatPerformanceUnitDisplay(visualizationParam?.unit, visualizationParam?.tooltip_unit)
  return `${computedLastValue}` + `<span>${unitText}</span>`
}

export const isUUID = (str: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

  return uuidRegex.test(str)
}

export const getIntegrationIds = (selectedIds: string[], isSnowflake: boolean, dataSourceOptions: any[]) => {
  if (!isSnowflake) {
    return selectedIds[0] === '' ? [''] : selectedIds
  }

  const areUUIDs = selectedIds.length > 0 && isUUID(selectedIds[0])

  return dataSourceOptions
    .filter((c) => {
      if (areUUIDs) {
        return selectedIds.includes(c.id || '')
      } else {
        return selectedIds.includes(c.instance_name || '')
      }
    })
    .map((c) => c.instance_name || '')
}
